import React, { useState, useEffect, createContext, useContext, useCallback, useRef, useMemo, memo } from 'react';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Auth Context
const AuthContext = createContext();

function AuthProvider({ children }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('staff_token');
    if (token) {
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const login = (token) => {
    localStorage.setItem('staff_token', token);
    setIsAuthenticated(true);
  };

  const logout = () => {
    localStorage.removeItem('staff_token');
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

function useAuth() {
  return useContext(AuthContext);
}

// Cart utilities
// Pulisce il carrello prima di salvarlo su localStorage
const sanitizeCartForStorage = (cart) => {
  const sanitizedCart = {};

  Object.entries(cart).forEach(([key, item]) => {
    const { product, ...rest } = item;

    const safeProduct = product ? {
      id: product.id,
      name: product.name,
      price: product.price,
      category: product.category,
      // se ti serve, puoi aggiungere:
      // description: product.description,
      // ma EVITA image_base64 e roba enorme
    } : null;

    sanitizedCart[key] = {
      ...rest,
      product: safeProduct,
    };
  });

  return sanitizedCart;
};

const saveCartToStorage = (cart) => {
  try {
    const lightCart = sanitizeCartForStorage(cart);
    const json = JSON.stringify(lightCart);
    localStorage.setItem('drive_in_cart', json);
  } catch (e) {
    if (e.name === 'QuotaExceededError') {
      localStorage.removeItem('drive_in_cart');
    }
  }
};

// Debounced version per non bloccare il main thread ad ogni click
let _saveTimer = null;
const debouncedSaveCart = (cart) => {
  if (_saveTimer) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => saveCartToStorage(cart), 300);
};


const loadCartFromStorage = () => {
  const saved = localStorage.getItem('drive_in_cart');
  return saved ? JSON.parse(saved) : {};
};

const clearCartFromStorage = () => {
  localStorage.removeItem('drive_in_cart');
  localStorage.removeItem('drive_in_session');
};

// Session management utilities
const SESSION_DURATION_ORDER = 10 * 60 * 1000; // 10 minuti per ordini
const SESSION_DURATION_CHECKOUT = 5 * 60 * 1000; // 5 minuti per checkout

const saveSessionToStorage = (type) => {
  const sessionData = {
    startTime: Date.now(),
    type: type, // 'order' o 'checkout'
    duration: type === 'checkout' ? SESSION_DURATION_CHECKOUT : SESSION_DURATION_ORDER
  };
  localStorage.setItem('drive_in_session', JSON.stringify(sessionData));
};

const getSessionFromStorage = () => {
  const saved = localStorage.getItem('drive_in_session');
  return saved ? JSON.parse(saved) : null;
};

const isSessionExpired = () => {
  const session = getSessionFromStorage();
  if (!session) return false;
  
  const elapsed = Date.now() - session.startTime;
  return elapsed > session.duration;
};

const getSessionExpiryInfo = () => {
  const session = getSessionFromStorage();
  if (!session) return { expired: false, timeSinceExpiry: 0 };
  
  const elapsed = Date.now() - session.startTime;
  const expired = elapsed > session.duration;
  
  if (!expired) {
    return { expired: false, timeSinceExpiry: 0 };
  }
  
  // Calcola quanto tempo è passato dalla scadenza
  const timeSinceExpiry = elapsed - session.duration;
  return { expired: true, timeSinceExpiry };
};

const clearExpiredSession = () => {
  if (isSessionExpired()) {
    clearCartFromStorage();
    return true;
  }
  return false;
};

// Components

function HomePage({ onNavigate, onShowLogin }) {
  const [serviceHours, setServiceHours] = useState({ start_time: '18:00', end_time: '23:00' });

  // Carica gli orari di servizio quando il componente si monta
  useEffect(() => {
    const fetchServiceHours = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/settings/hours`);
        if (response.ok) {
          const hoursData = await response.json();
          setServiceHours({
            start_time: hoursData.start_time,
            end_time: hoursData.end_time
          });
        }
      } catch (error) {
        console.error('Error fetching service hours:', error);
      }
    };

    fetchServiceHours();
  }, []);

  const checkOrdersStatus = async () => {
    try {
      const statusResponse = await fetch(`${BACKEND_URL}/api/settings/status`);
      const statusData = await statusResponse.json();
      
      // Naviga sempre al menu, passando lo stato delle ordinazioni
      onNavigate('order', { ordersEnabled: statusData.orders_enabled });
    } catch (error) {
      console.error('Error checking orders status:', error);
      // In caso di errore, procedi comunque con ordinazioni abilitate
      onNavigate('order', { ordersEnabled: true });
    }
  };

  return (
    <div className="home-container">
      <div className="starry-background">
        <img 
          src="https://images.unsplash.com/photo-1628498188873-579210ce622e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxzdGFycnklMjBuaWdodHxlbnwwfHx8fDE3NTQ0ODY2MjB8MA&ixlib=rb-4.1.0&q=85"
          alt="Starry Night Sky"
          className="background-video"
        />
        <div className="stars-overlay"></div>
      </div>
      
      <div className="content-overlay">
        <div className="header-section">
          <div className="social-links">
            <a 
              href="https://www.instagram.com/drivein_pozzuoli/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link instagram"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_drivethru-cinema/artifacts/eyz2w72q_instagram-logo-instagram-icon-transparent-free-png.webp" 
                alt="Instagram" 
              />
            </a>
            
            <a 
              href="https://www.tiktok.com/@driveinpozzuoli" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link tiktok"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_drivethru-cinema/artifacts/hy1jyjc0_tik-tok-how-use-tiktok-create-cool-videos-with-iphone-14.png" 
                alt="TikTok" 
              />
            </a>
            
            <a 
              href="https://m.facebook.com/driveinpozzuoli/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="social-link facebook"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_drivethru-cinema/artifacts/ekcql48n_facebook-logo-facebook-icon-transparent-free-png.webp" 
                alt="Facebook" 
              />
            </a>
          </div>
          
          <button className="login-button" onClick={onShowLogin}>
            Login
          </button>
        </div>
        
        <div className="logo-container">
          <img 
            src="https://customer-assets.emergentagent.com/job_cinemadrive/artifacts/4dw3lyzj_logo_drive.png" 
            alt="Drive In Logo" 
            className="logo"
          />
        </div>
        
        <div className="buttons-container">
          <button 
            className="main-button buy-tickets"
            onClick={() => window.open('https://drivein.18tickets.it/', '_blank')}
          >
            🎫 Acquista Biglietti
          </button>
          
          <button 
            className="main-button order-food"
            onClick={checkOrdersStatus}
          >
            🍔 Ordina Menù
          </button>
          
          {/* Frase dinamica con orario di chiusura */}
          <div className="kitchen-hours-info">
            <p>La cucina chiude alle ore <strong>{serviceHours.end_time}</strong></p>
          </div>
        </div>
      </div>
    </div>
  );
}

function OrdersDisabledModal({ isOpen, onClose, serviceHours, disabledReason }) {
  if (!isOpen) return null;

  const { start_time = '18:00', end_time = '23:00' } = serviceHours || {};

  // Messaggio diverso a seconda del motivo
  const renderMessage = () => {
    if (disabledReason === 'manually_disabled') {
      return (
        <div className="orders-disabled-message">
          <p className="main-message">Le ordinazioni sono temporaneamente sospese.</p>
          <p className="apology-message">Per qualsiasi necessita rivolgersi allo staff</p>
        </div>
      );
    } else {
      // outside_hours o default
      return (
        <div className="orders-disabled-message">
          <p className="main-message">Le ordinazioni non sono al momento disponibili.</p>
          <p className="info-message">È possibile ordinare dalle ore <strong>{start_time}</strong> alle ore <strong>{end_time}</strong></p>
        </div>
      );
    }
  };

  return (
    <div className="modal-overlay orders-disabled-overlay" onClick={onClose}>
      <div className="modal-content orders-disabled-modal" onClick={e => e.stopPropagation()}>
        <div className="orders-disabled-icon">
          {disabledReason === 'manually_disabled' ? '❌' : '🕐'}
        </div>
        <h2 className="orders-disabled-title">
          {disabledReason === 'manually_disabled' ? 'Ordinazioni Sospese' : 'Ordinazioni Temporaneamente Chiuse'}
        </h2>
        {renderMessage()}
        <button className="orders-disabled-button" onClick={onClose}>
          Ho Capito
        </button>
      </div>
    </div>
  );
}

function LoginModal({ isOpen, onClose, onLogin }) {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/staff/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(credentials)
      });

      if (response.ok) {
        const data = await response.json();
        onLogin(data.access_token);
        onClose();
      } else {
        setError('Credenziali non valide');
      }
    } catch (error) {
      setError('Errore di connessione');
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <h2>Login Staff</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="text"
            placeholder="Username"
            value={credentials.username}
            onChange={(e) => setCredentials({...credentials, username: e.target.value})}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={credentials.password}
            onChange={(e) => setCredentials({...credentials, password: e.target.value})}
            required
          />
          {error && <div className="error-message">{error}</div>}
          <div className="modal-buttons">
            <button type="button" onClick={onClose}>Annulla</button>
            <button type="submit" disabled={loading}>
              {loading ? 'Accesso...' : 'Login'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function MenuSelectionModal({ isOpen, onClose, onSelectOptions, menuProduct }) {
  const [selectedDrink, setSelectedDrink] = useState(null);
  const [selectedFries, setSelectedFries] = useState(null);
  const [sauceSelections, setSauceSelections] = useState([]);

  useEffect(() => {
    if (isOpen && menuProduct) {
      // Reset selections when modal opens
      setSelectedDrink(null);
      setSelectedFries(null);

      // Auto-select first options if available
      if (menuProduct.menu_options?.fries_options?.length > 0) {
        setSelectedFries(menuProduct.menu_options.fries_options[0]);
      }
      if (menuProduct.menu_options?.drink_options?.length > 0) {
        setSelectedDrink(menuProduct.menu_options.drink_options[0]);
      }

      // Inizializza salse con quantity = 0
      if (menuProduct.menu_options?.sauces_options?.length > 0) {
        setSauceSelections(
          menuProduct.menu_options.sauces_options.map(sauce => ({
            ...sauce,
            quantity: 0,
          }))
        );
      } else {
        setSauceSelections([]);
      }
    }
  }, [isOpen, menuProduct]);

  const changeSauceQuantity = (sauceName, delta) => {
    setSauceSelections(prev =>
      prev.map(s =>
        s.name === sauceName
          ? { ...s, quantity: Math.max(0, (s.quantity || 0) + delta) }
          : s
      )
    );
  };

  if (!isOpen || !menuProduct) return null;

  const { menu_options } = menuProduct;
  if (!menu_options || !menu_options.fries_options || !menu_options.drink_options) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <h2>Menu non configurato</h2>
          <p>Questo menu non ha opzioni configurate. Contatta lo staff.</p>
          <div className="modal-buttons">
            <button onClick={onClose}>Chiudi</button>
          </div>
        </div>
      </div>
    );
  }

  const basePrice = parseFloat(menuProduct.price) || 0;
  const drinkModifier = selectedDrink ? parseFloat(selectedDrink.price_modifier) || 0 : 0;
  const friesModifier = selectedFries ? parseFloat(selectedFries.price_modifier) || 0 : 0;
  const saucesTotalModifier = sauceSelections.reduce((sum, sauce) => {
    const price = parseFloat(sauce.price_modifier) || 0;
    const qty = sauce.quantity || 0;
    return sum + price * qty;
  }, 0);
  const totalPrice = basePrice + drinkModifier + friesModifier + saucesTotalModifier;

  const handleConfirm = () => {
    if (!selectedDrink || !selectedFries) {
      alert('Seleziona sia le patatine che la bibita');
      return;
    }

    onSelectOptions({
      drink: selectedDrink,
      fries: selectedFries,
      sauces: sauceSelections.filter(s => (s.quantity || 0) > 0),
      drink_modifier: drinkModifier,
      fries_modifier: friesModifier,
      menu_sauces_modifier: saucesTotalModifier,
      totalPrice: totalPrice,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content menu-selection-modal" onClick={e => e.stopPropagation()}>
        <h2>Personalizza il tuo {menuProduct.name}</h2>
        <p className="menu-base-price">Prezzo base: €{basePrice.toFixed(2)}</p>

        <div className="menu-option-section">
          <h3>Scegli le Patatine</h3>
          <div className="options-grid">
            {menu_options.fries_options.map((fries, index) => (
              <div
                key={index}
                className={`menu-option ${selectedFries?.name === fries.name ? 'selected' : ''}`}
                onClick={() => setSelectedFries(fries)}
              >
                <span className="option-name">{fries.name}</span>
                <span className="option-price">
                  {parseFloat(fries.price_modifier) > 0
                    ? `+€${parseFloat(fries.price_modifier).toFixed(2)}`
                    : 'Incluso'}
                </span>
              </div>
            ))}
          </div>
        </div>

        {menu_options.sauces_options && menu_options.sauces_options.length > 0 && (
          <div className="menu-option-section">
            <h3>Scegli le Salse</h3>
            <div className="ingredients-grid">
              {sauceSelections.map((sauce, index) => (
                <div key={index} className="ingredient-option">
                  <div className="ingredient-info">
                    <span className="option-name">{sauce.name}</span>
                    <span className="option-price">
                      {parseFloat(sauce.price_modifier) > 0
                        ? `+€${parseFloat(sauce.price_modifier).toFixed(2)}`
                        : 'Gratis'}
                    </span>
                  </div>
                  <div className="ingredient-qty-controls">
                    <button
                      type="button"
                      className="qty-btn"
                      onClick={() => changeSauceQuantity(sauce.name, -1)}
                    >
                      -
                    </button>
                    <span className="quantity">{sauce.quantity || 0}</span>
                    <button
                      type="button"
                      className="qty-btn"
                      onClick={() => changeSauceQuantity(sauce.name, 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="menu-option-section">
          <h3>Scegli la Bibita</h3>
          <div className="options-grid">
            {menu_options.drink_options.map((drink, index) => (
              <div 
                key={index} 
                className={`menu-option ${selectedDrink?.name === drink.name ? 'selected' : ''}`}
                onClick={() => setSelectedDrink(drink)}
              >
                <span className="option-name">{drink.name}</span>
                <span className="option-price">
                  {parseFloat(drink.price_modifier) > 0 ? 
                    `+€${parseFloat(drink.price_modifier).toFixed(2)}` : 
                    'Incluso'}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'sticky', bottom: 0, background: 'var(--bg-surface)' }}>
          <div className="modal-buttons">
            <button type="button" onClick={onClose}>
              Annulla
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="confirm-btn"
              disabled={!selectedDrink || !selectedFries}
            >
              Aggiungi al carrello
            </button>
          </div>

          <div className="menu-total">
            <strong>Totale: €{totalPrice.toFixed(2)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}

// Limiti ingredienti SOLO per le patatine
const PATATINE_INGREDIENT_LIMITS = {
  "Cheddar Fuso": 1, // cambia la stringa se nel backend ha un nome diverso
  // "Extra Cheddar Fuso": 1,
};

// Componente modale per personalizzazione patatine
function PatatineSelectionModal({ isOpen, onClose, onSelectIngredients, patatineProduct }) {
  const [ingredientSelections, setIngredientSelections] = useState([]);

  useEffect(() => {
    if (isOpen && patatineProduct) {
      const { ingredients_options } = patatineProduct;
      if (ingredients_options && ingredients_options.length > 0) {
        setIngredientSelections(
          ingredients_options.map(ing => ({
            ...ing,
            quantity: 0,
          }))
        );
      } else {
        setIngredientSelections([]);
      }
    }
  }, [isOpen, patatineProduct]);

  if (!isOpen || !patatineProduct) return null;

  const { ingredients_options } = patatineProduct;
  if (!ingredients_options || ingredients_options.length === 0) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <h2>Patatine non configurate</h2>
          <p>Queste patatine non hanno ingredienti extra configurati.</p>
          <div className="modal-buttons">
            <button onClick={onClose}>Chiudi</button>
          </div>
        </div>
      </div>
    );
  }

  const basePrice = parseFloat(patatineProduct.price) || 0;
  const ingredientsTotalModifier = ingredientSelections.reduce((sum, ing) => {
    const price = parseFloat(ing.price_modifier) || 0;
    const qty = ing.quantity || 0;
    return sum + price * qty;
  }, 0);
  const totalPrice = basePrice + ingredientsTotalModifier;

  const changeIngredientQuantity = (ingredientName, delta) => {
    setIngredientSelections(prev =>
      prev.map(ing => {
        if (ing.name !== ingredientName) return ing;

        const currentQty = ing.quantity || 0;
        const maxQty = PATATINE_INGREDIENT_LIMITS[ingredientName];

        let newQty = currentQty + delta;

        // se esiste un limite per questo ingrediente, clamp tra 0 e max
        if (maxQty !== undefined) {
          newQty = Math.min(maxQty, newQty);
        }

        return {
          ...ing,
          quantity: Math.max(0, newQty),
        };
      })
    );
  };

  const handleConfirm = () => {
    onSelectIngredients({
      ingredients: ingredientSelections.filter(i => (i.quantity || 0) > 0),
      ingredients_modifier: ingredientsTotalModifier,
      totalPrice: totalPrice,
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content patatine-selection-modal" onClick={e => e.stopPropagation()}>
        <h2>Personalizza le tue {patatineProduct.name}</h2>
        <p className="menu-base-price">
          Prezzo base: €{basePrice.toFixed(2)}
        </p>

        <div className="menu-option-section">
          <h3>Aggiungi Ingredienti Extra</h3>
          <div className="ingredients-grid">
            {ingredientSelections.map((ingredient, index) => (
              <div key={index} className="ingredient-option">
                <div className="ingredient-info">
                  <span className="option-name">{ingredient.name}</span>
                  <span className="option-price">
                    {parseFloat(ingredient.price_modifier) > 0
                      ? `+€${parseFloat(ingredient.price_modifier).toFixed(2)}`
                      : 'Gratis'}
                  </span>
                </div>
                <div className="ingredient-qty-controls">
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => changeIngredientQuantity(ingredient.name, -1)}
                  >
                    -
                  </button>
                  <span className="quantity">{ingredient.quantity || 0}</span>
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => changeIngredientQuantity(ingredient.name, 1)}
                    disabled={
                      PATATINE_INGREDIENT_LIMITS[ingredient.name] !== undefined &&
                      (ingredient.quantity || 0) >= PATATINE_INGREDIENT_LIMITS[ingredient.name]
                    }
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'sticky', bottom: 0, background: 'var(--bg-surface)' }}>
          <div className="modal-buttons">
            <button type="button" onClick={onClose}>
              Annulla
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="confirm-btn"
            >
              Aggiungi al carrello
            </button>
          </div>

          <div className="menu-total">
            <strong>Totale: €{totalPrice.toFixed(2)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}


// Componente modale per personalizzazione panini
function PaniniSelectionModal({ isOpen, onClose, onSelectSauces, paniniProduct }) {
  const [sauceSelections, setSauceSelections] = useState([]);

  useEffect(() => {
    if (isOpen && paniniProduct && paniniProduct.sauces_options) {
      // inizializzo le salse con quantity = 0
      setSauceSelections(
        paniniProduct.sauces_options.map(s => ({
          ...s,
          quantity: 0,
        }))
      );
    }
  }, [isOpen, paniniProduct]);
  
  const changeSauceQuantity = (sauceName, delta) => {
    setSauceSelections(prev =>
      prev.map(s =>
        s.name === sauceName
          ? { ...s, quantity: Math.max(0, (s.quantity || 0) + delta) }
          : s
      )
    );
  };

  const saucesTotalModifier = sauceSelections.reduce(
    (sum, s) =>
      sum +
      (parseFloat(s.price_modifier) || 0) * (s.quantity ? Number(s.quantity) : 0),
    0
  );

  const totalPrice =
    (parseFloat(paniniProduct?.price) || 0) + saucesTotalModifier;

  const handleConfirm = () => {
    const selectedSauces = sauceSelections.filter(
      s => (s.quantity || 0) > 0
    );

    onSelectSauces({
      sauces: selectedSauces,           // 👈 array di oggetti {name, price_modifier, quantity}
      sauces_modifier: saucesTotalModifier,
      totalPrice: totalPrice,
    });

    onClose();
  };

  if (!isOpen || !paniniProduct) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content panini-selection-modal"
        onClick={e => e.stopPropagation()}
      >
        <h2>Personalizza il tuo {paniniProduct.name}</h2>
        <p className="menu-base-price">
          Prezzo base: €{parseFloat(paniniProduct.price).toFixed(2)}
        </p>

        <div className="menu-option-section">
          <h3>Scegli le Salse</h3>
          <div className="ingredients-grid">
            {sauceSelections.map((sauce, index) => (
              <div key={index} className="ingredient-option">
                <div className="ingredient-info">
                  <span className="option-name">{sauce.name}</span>
                  <span className="option-price">
                    {parseFloat(sauce.price_modifier) > 0
                      ? `+€${parseFloat(sauce.price_modifier).toFixed(2)}`
                      : 'Gratis'}
                  </span>
                </div>
                <div className="ingredient-qty-controls">
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => changeSauceQuantity(sauce.name, -1)}
                  >
                    -
                  </button>
                  <span className="quantity">{sauce.quantity || 0}</span>
                  <button
                    type="button"
                    className="qty-btn"
                    onClick={() => changeSauceQuantity(sauce.name, 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ position: 'sticky', bottom: 0, background: 'var(--bg-surface)' }}>
          <div className="modal-buttons">
            <button type="button" onClick={onClose}>
              Annulla
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="confirm-btn"
            >
              Aggiungi al carrello
            </button>
          </div>

          <div className="menu-total">
            <strong>Totale: €{totalPrice.toFixed(2)}</strong>
          </div>
        </div>
      </div>
    </div>
  );
}


// ProductCard memoizzato - definito fuori dal componente per evitare re-creazione
const ProductCard = memo(({ product, quantity, ordersEnabled, cartQtyForProduct, onMenuSelect, onPatatineSelect, onPaniniSelect, onUpdateQty }) => {
  const isAvailable = product.available !== false && ordersEnabled;
  const hasStock = product.stock_quantity != null;
  const remaining = hasStock ? product.stock_quantity - cartQtyForProduct : Infinity;
  const atMax = remaining <= 0;
  
  return (
    <div className={`product-card ${!isAvailable ? 'product-unavailable-card' : ''}`}>
      <div className="product-image">
        {product.image_base64 ? (
          <img src={`data:image/jpeg;base64,${product.image_base64}`} alt={product.name} loading="lazy" />
        ) : (
          <div className="no-image">📷</div>
        )}
        {product.available === false && (
          <div className="unavailable-overlay">
            <span className="unavailable-badge">Non disponibile</span>
          </div>
        )}
      </div>
      <div className="product-info">
        <h3>{product.name}</h3>
        <p className="product-description">{product.description}</p>
        {product.available !== false ? (
          <>
            <p className="product-price">€{product.price.toFixed(2)}</p>
            {hasStock && (
              <p className={`stock-remaining ${remaining <= 0 ? 'stock-remaining-empty' : remaining <= 3 ? 'stock-remaining-low' : ''}`}>
                Rimasti: {Math.max(0, remaining)}
              </p>
            )}
          </>
        ) : (
          <p className="product-unavailable">Non disponibile</p>
        )}
      </div>
      <div className="quantity-controls">
        {isAvailable ? (
          product.category === 'menu' ? (
            <button 
              className={`add-menu-btn ${atMax ? 'disabled' : ''}`}
              onClick={() => !atMax && onMenuSelect(product)}
              disabled={atMax}
            >
              {atMax ? 'Esaurito' : 'Personalizza'}
            </button>
          ) : product.category === 'patatine' ? (
            <button 
              className={`add-patatine-btn ${atMax ? 'disabled' : ''}`}
              onClick={() => !atMax && onPatatineSelect(product)}
              disabled={atMax}
            >
              {atMax ? 'Esaurito' : 'Personalizza'}
            </button>
          ) : product.category === 'panini' && product.sauces_options ? (
            <button 
              className={`add-panini-btn ${atMax ? 'disabled' : ''}`}
              onClick={() => !atMax && onPaniniSelect(product)}
              disabled={atMax}
            >
              {atMax ? 'Esaurito' : 'Personalizza'}
            </button>
          ) : (
            <>
              <button 
                className="qty-btn"
                onClick={() => onUpdateQty(product.id, -1)}
                disabled={!quantity}
              >
                -
              </button>
              <span className="quantity">{quantity}</span>
              <button 
                className="qty-btn"
                onClick={() => onUpdateQty(product.id, 1)}
                disabled={atMax}
              >
                +
              </button>
            </>
          )
        ) : (
          <div className="unavailable-controls">
          </div>
        )}
      </div>
    </div>
  );
}, (prev, next) => {
  return prev.product.id === next.product.id 
    && prev.quantity === next.quantity 
    && prev.ordersEnabled === next.ordersEnabled
    && prev.product.available === next.product.available
    && prev.product.stock_quantity === next.product.stock_quantity
    && prev.cartQtyForProduct === next.cartQtyForProduct;
});


function OrderPage({ onNavigate, ordersEnabled = true }) {
  const [products, setProducts] = useState({ panini: [], patatine: [], sfizi: [], bibite: [], menu: [], dolci: [] });
  const [cart, setCart] = useState(loadCartFromStorage());
  const [loading, setLoading] = useState(true); // Sempre inizia con loading = true
  const [activeCategory, setActiveCategory] = useState('menu');
  const [showMenuModal, setShowMenuModal] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [showPatatineModal, setShowPatatineModal] = useState(false);
  const [selectedPatatine, setSelectedPatatine] = useState(null);
  const [showPaniniModal, setShowPaniniModal] = useState(false);
  const [selectedPanini, setSelectedPanini] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    // Assicura che loading sia sempre true all'inizio
    setLoading(true);
    
    // Ottimizzazione: controlli sessione più rapidi
    const expiryInfo = getSessionExpiryInfo();
    
    if (expiryInfo.expired) {
      const FIVE_MINUTES = 5 * 60 * 1000;
      
      if (expiryInfo.timeSinceExpiry <= FIVE_MINUTES) {
        setSessionExpired(true);
        return;
      } else {
        // Reset rapido senza attese
        clearCartFromStorage();
        setCart({});
        setShowMenuModal(false);
        setSelectedMenu(null);
      }
    }

    // Avvia subito sessione e caricamento in parallelo
    saveSessionToStorage('order');
    fetchProducts(); // Avvio immediato

    // Controlli sessione molto meno frequenti per evitare sovraccarico backend
    const sessionInterval = setInterval(() => {
      if (isSessionExpired()) {
        setSessionExpired(true);
        clearInterval(sessionInterval);
      }
    }, 300000); // Controlli ogni 5 minuti invece di 1 minuto

    return () => clearInterval(sessionInterval);
  }, []);

  useEffect(() => {
    debouncedSaveCart(cart);
  }, [cart]);

  // IntersectionObserver: aggiorna la categoria attiva durante lo scroll
  const categoryNavRef = useRef(null);
  useEffect(() => {
    if (loading) return;
    const categories = ['menu', 'panini', 'patatine', 'sfizi', 'dolci', 'bibite'];
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.id.replace('category-', '');
          setActiveCategory(id);
          // Scroll del bottone attivo nella barra di navigazione
          if (categoryNavRef.current) {
            const btn = categoryNavRef.current.querySelector(`.category-btn.active, [data-cat="${id}"]`);
            if (btn) {
              btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
            }
          }
        }
      });
    }, { rootMargin: '-30% 0px -60% 0px', threshold: 0 });

    categories.forEach(cat => {
      const el = document.getElementById(`category-${cat}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [loading]);

  const fetchProducts = async () => {
    try {
      const [response] = await Promise.all([
        fetch(`${BACKEND_URL}/api/products`),
        new Promise(resolve => setTimeout(resolve, 1500))
      ]);
      
      const data = await response.json();
      setProducts(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setTimeout(() => setLoading(false), 1500);
    }
  };

  const updateQuantity = useCallback((productId, change) => {
    setCart(prev => {
      const newCart = { ...prev };
      const currentQty = newCart[productId]?.quantity || 0;
      const newQty = Math.max(0, currentQty + change);
      
      if (newQty === 0) {
        delete newCart[productId];
      } else {
        const allProducts = [...products.panini, ...products.sfizi, ...products.bibite, ...products.menu, ...products.dolci];
        const product = allProducts.find(p => p.id === productId);
        if (product) {
          newCart[productId] = {
            ...newCart[productId],
            quantity: newQty,
            product: product
          };
        }
      }
      return newCart;
    });
  }, [products]);

  const handleMenuSelection = useCallback((product) => {
    setShowPatatineModal(false);
    setSelectedPatatine(null);
    setSelectedMenu(product);
    setShowMenuModal(true);
  }, []);

  const handlePaniniSelection = useCallback((panino) => {
    setShowMenuModal(false);
    setSelectedMenu(null);
    setShowPatatineModal(false);
    setSelectedPatatine(null);
    setSelectedPanini(panino);
    setShowPaniniModal(true);
  }, []);

  const handlePatatineSelection = useCallback((patatine) => {
    setShowMenuModal(false);
    setSelectedMenu(null);
    setSelectedPatatine(patatine);
    setShowPatatineModal(true);
  }, []);

  // Calcola quantità totale nel carrello per un dato product.id
  const getCartQtyForProduct = useCallback((productId) => {
    let total = 0;
    Object.values(cart).forEach(item => {
      if (item.product && item.product.id === productId) {
        total += item.quantity || 0;
      }
    });
    return total;
  }, [cart]);

  // Sessione scaduta - return DOPO tutti gli hooks
  if (sessionExpired) {
    return (
      <div className="session-expired-page">
        <div className="session-expired-content">
          <div className="expired-icon">⏰</div>
          <h2>Sessione Scaduta</h2>
          <p>La tua sessione di ordinazione è scaduta per inattività.</p>
          <p>Puoi ricominciare a ordinare quando vuoi!</p>
          <button 
            className="restart-button"
            onClick={() => {
              clearCartFromStorage();
              window.location.reload();
            }}
          >
            Ricomincia Ordinazione
          </button>
          <button 
            className="home-button-secondary"
            onClick={() => onNavigate('home')}
          >
            Torna alla Home
          </button>
        </div>
      </div>
    );
  }

  const handlePatatineOptions = (options) => {
  if (selectedPatatine && options) {
    const cartKey = `patatine_${selectedPatatine.id}_${Date.now()}`;
    setCart(prev => {
      const newCart = { ...prev };

      newCart[cartKey] = {
        quantity: 1,
        product: selectedPatatine,
        // array di oggetti { name, quantity, price_modifier }
        selectedIngredients: options.ingredients,
        ingredientsModifiers: options.ingredients_modifier || 0,
        totalPrice: options.totalPrice,
      };

      return newCart;
    });
  }
  setShowPatatineModal(false);
  setSelectedPatatine(null);
};



  const handlePaniniOptions = (options) => {
  if (selectedPanini && options) {
    const cartKey = `panini_${selectedPanini.id}_${Date.now()}`;
    setCart(prev => {
      const newCart = { ...prev };

      const selectedSaucesForCart = options.sauces.map((sauce) => ({
        name: sauce.name,
        quantity: sauce.quantity || 1,
        price_modifier: sauce.price_modifier || 0,
      }));

      const saucesModifiers = selectedSaucesForCart.reduce(
        (sum, sauce) =>
          sum +
          (parseFloat(sauce.price_modifier) || 0) *
            (sauce.quantity ? Number(sauce.quantity) : 1),
        0
      );

      newCart[cartKey] = {
        quantity: 1,
        product: selectedPanini,
        selectedSauces: selectedSaucesForCart, // 👈 array di oggetti
        saucesModifiers,
        totalPrice: options.totalPrice,
      };

      return newCart;
    });
  }
  setShowPaniniModal(false);
  setSelectedPanini(null);
};


  const handleMenuOptions = (options) => {
  if (selectedMenu && options && options.drink && options.fries) {
    const cartKey = `${selectedMenu.id}_${Date.now()}`;
    setCart(prev => {
      const newCart = { ...prev };

      newCart[cartKey] = {
        quantity: 1,
        product: selectedMenu,
        selectedDrink: options.drink.name,
        selectedFries: options.fries.name,
        // array di oggetti { name, quantity, price_modifier }
        selectedMenuSauces: options.sauces,
        drinkModifier: options.drink_modifier || 0,
        friesModifier: options.fries_modifier || 0,
        menuSaucesModifier: options.menu_sauces_modifier || 0,
        totalPrice: options.totalPrice,
      };

      return newCart;
    });
  }
  setShowMenuModal(false);
  setSelectedMenu(null);
};


  const getCartTotal = () => {
    let total = 0;
    Object.keys(cart).forEach(cartKey => {
      const cartItem = cart[cartKey];
      if (cartItem.product) {
        if (cartItem.totalPrice) {
          // Menu item with custom pricing
          total += cartItem.totalPrice * cartItem.quantity;
        } else {
          // Regular item
          total += cartItem.product.price * cartItem.quantity;
        }
      }
    });
    return total;
  };

  const findProductById = (id) => {
    const allProducts = [...products.panini, ...products.sfizi, ...products.bibite, ...products.menu, ...products.dolci];
    return allProducts.find(p => p.id === id);
  };

  const getCartItemsCount = () => {
    return Object.values(cart).reduce((sum, item) => sum + item.quantity, 0);
  };

  const scrollToCategory = (category) => {
    setActiveCategory(category);
    const element = document.getElementById(`category-${category}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const MenuItemCard = ({ cartKey, cartItem }) => {
    const { product, selectedDrink, selectedFries, selectedMenuSauces, quantity, totalPrice } = cartItem;
    const totalInCart = getCartQtyForProduct(product.id);
    const hasStock = product.stock_quantity != null;
    const remaining = hasStock ? product.stock_quantity - totalInCart : Infinity;
    const atMax = remaining <= 0;
    
    return (
      <div className="menu-item-card">
        <div className="menu-item-info">
          <h4>{product.name}</h4>
          {selectedFries && <p>Patatine: {selectedFries}</p>}
          {selectedDrink && <p>Bibita: {selectedDrink}</p>}
          {selectedMenuSauces && selectedMenuSauces.length > 0 && (
            <p>
              Salse:{' '}
              {selectedMenuSauces
                .filter(s => (s.quantity || 0) > 0)
                .map(s => (s.quantity || 0) > 1 ? `${s.name} x${s.quantity}` : s.name)
                .join(', ')}
            </p>
          )}
          <p>€{(totalPrice || product.price).toFixed(2)}</p>
        </div>
        <div className="quantity-controls">
          <button 
            className="qty-btn"
            onClick={(e) => {
              e.stopPropagation();
              setCart(prev => {
                const newCart = { ...prev };
                
                if (newCart[cartKey].quantity > 1) {
                  newCart[cartKey].quantity -= 1;
                } else {
                  delete newCart[cartKey];
                }
                return newCart;
              });
            }}
          >
            -
          </button>
          <span className="quantity">{quantity}</span>
          <button 
            className="qty-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (atMax) return;
              setCart(prev => {
                const newCart = {
                  ...prev,
                  [cartKey]: { ...prev[cartKey], quantity: quantity + 1 }
                };
                return newCart;
              });
            }}
            disabled={atMax}
          >
            +
          </button>
        </div>
      </div>
    );
  };

  const PatatineItemCard = ({ cartKey, cartItem }) => {
    const { product, selectedIngredients, quantity, totalPrice } = cartItem;
    
    return (
      <div className="patatine-item-card">
        <div className="patatine-item-info">
          <h4>{product.name}</h4>
          {selectedIngredients && selectedIngredients.length > 0 && (
          <div className="ingredients-list">
            <small>
              Extra:{' '}
              {selectedIngredients
                .filter(i => (i.quantity || 0) > 0)
                .map(i => (i.quantity || 0) > 1 ? `${i.name} x${i.quantity}` : i.name)
                .join(', ')}
            </small>
          </div>
        )}

          <p>€{(totalPrice || product.price).toFixed(2)}</p>
        </div>
        <div className="quantity-controls">
          <button 
            className="qty-btn"
            onClick={(e) => {
              e.stopPropagation();
              setCart(prev => {
                const newCart = { ...prev };
                if (newCart[cartKey].quantity > 1) {
                  newCart[cartKey].quantity -= 1;
                } else {
                  delete newCart[cartKey];
                }
                return newCart;
              });
            }}
          >
            -
          </button>
          <span className="quantity">{quantity}</span>
          <button 
            className="qty-btn"
            onClick={(e) => {
              e.stopPropagation();
              setCart(prev => {
                const newCart = {
                  ...prev,
                  [cartKey]: { ...prev[cartKey], quantity: quantity + 1 }
                };
                return newCart;
              });
            }}
          >
            +
          </button>
        </div>
      </div>
    );
  };

  const PaniniItemCard = ({ cartKey, cartItem }) => {
    const { product, selectedSauces, quantity, totalPrice } = cartItem;
    
    return (
      <div className="panini-item-card">
        <div className="panini-item-info">
          <h4>{product.name}</h4>
          {selectedSauces && selectedSauces.length > 0 && (
          <div className="sauces-list">
            <small>
              Salse:{' '}
              {selectedSauces
                .filter(s => (s.quantity || 0) > 0)
                .map(s => (s.quantity || 0) > 1 ? `${s.name} x${s.quantity}` : s.name)
                .join(', ')}
            </small>
          </div>
        )}

          <p>€{(totalPrice || product.price).toFixed(2)}</p>
        </div>
        <div className="quantity-controls">
          <button 
            className="qty-btn"
            onClick={(e) => {
              e.stopPropagation();
              setCart(prev => {
                const newCart = { ...prev };
                if (newCart[cartKey].quantity > 1) {
                  newCart[cartKey].quantity -= 1;
                } else {
                  delete newCart[cartKey];
                }
                return newCart;
              });
            }}
          >
            -
          </button>
          <span className="quantity">{quantity}</span>
          <button 
            className="qty-btn"
            onClick={(e) => {
              e.stopPropagation();
              setCart(prev => {
                const newCart = {
                  ...prev,
                  [cartKey]: { ...prev[cartKey], quantity: quantity + 1 }
                };
                return newCart;
              });
            }}
          >
            +
          </button>
        </div>
      </div>
    );
  };
  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-road">
            <div className="car-animation">
              🚗
            </div>
          </div>
          <h2 className="loading-title">Caricamento Menu...</h2>
          <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-page">
      <div className="order-header">
        <button className="back-button" onClick={() => onNavigate('home')}>
          ← Home
        </button>
        <h1>Ordina da Menu</h1>
      </div>

      <div className="category-nav" ref={categoryNavRef}>
        {['menu', 'panini', 'patatine', 'sfizi', 'dolci', 'bibite'].map(category => (
          <button
            key={category}
            data-cat={category}
            className={`category-btn ${activeCategory === category ? 'active' : ''}`}
            onClick={() => scrollToCategory(category)}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>


      {!ordersEnabled && (
        <div className="kitchen-closed-banner" data-testid="kitchen-closed-banner">
          <span className="kitchen-closed-icon">🕐</span>
          <span>La cucina è attualmente chiusa. Puoi consultare il menu ma non è possibile ordinare.</span>
        </div>
      )}

      <div className="menu-sections">
        <div id="category-menu" className="section">
  <h2>📋 Menu</h2>
  <div className="products-grid">
    {products.menu.map(product => (
      <ProductCard key={product.id} product={product} quantity={cart[product.id]?.quantity || 0} ordersEnabled={ordersEnabled} cartQtyForProduct={getCartQtyForProduct(product.id)} onMenuSelect={handleMenuSelection} onPatatineSelect={handlePatatineSelection} onPaniniSelect={handlePaniniSelection} onUpdateQty={updateQuantity} />
    ))}
  </div>
  
  {Object.keys(cart)
    .filter(
      key =>
        key.includes('_') &&
        !key.startsWith('patatine_') &&
        !key.startsWith('panini_')
    ).length > 0 && (
      <div className="selected-menus">
        <h3>Menu Selezionati:</h3>
        {Object.keys(cart)
          .filter(
            key =>
              key.includes('_') &&
              !key.startsWith('patatine_') &&
              !key.startsWith('panini_')
          )
          .map(cartKey => (
            <MenuItemCard
              key={cartKey}
              cartKey={cartKey}
              cartItem={cart[cartKey]}
            />
          ))}
      </div>
  )}
</div>


        <div id="category-panini" className="section">
          <h2>🍔 Panini</h2>
          <div className="products-grid">
            {products.panini.map(product => (
              <ProductCard key={product.id} product={product} quantity={cart[product.id]?.quantity || 0} ordersEnabled={ordersEnabled} cartQtyForProduct={getCartQtyForProduct(product.id)} onMenuSelect={handleMenuSelection} onPatatineSelect={handlePatatineSelection} onPaniniSelect={handlePaniniSelection} onUpdateQty={updateQuantity} />
            ))}
          </div>
          
          {Object.keys(cart).filter(key => key.startsWith('panini_')).length > 0 && (
            <div className="selected-panini">
              <h3>Panini Personalizzati:</h3>
              {Object.keys(cart)
                .filter(key => key.startsWith('panini_'))
                .map(cartKey => (
                  <PaniniItemCard key={cartKey} cartKey={cartKey} cartItem={cart[cartKey]} />
                ))
              }
            </div>
          )}
        </div>

        <div id="category-patatine" className="section">
          <h2>🍟 Patatine</h2>
          <div className="products-grid">
            {products.patatine.map(product => (
              <ProductCard key={product.id} product={product} quantity={cart[product.id]?.quantity || 0} ordersEnabled={ordersEnabled} cartQtyForProduct={getCartQtyForProduct(product.id)} onMenuSelect={handleMenuSelection} onPatatineSelect={handlePatatineSelection} onPaniniSelect={handlePaniniSelection} onUpdateQty={updateQuantity} />
            ))}
          </div>
          
          {Object.keys(cart).filter(key => key.includes('patatine_')).length > 0 && (
            <div className="selected-patatine">
              <h3>Patatine Personalizzate:</h3>
              {Object.keys(cart)
                .filter(key => key.includes('patatine_'))
                .map(cartKey => (
                  <PatatineItemCard key={cartKey} cartKey={cartKey} cartItem={cart[cartKey]} />
                ))
              }
            </div>
          )}
        </div>

        <div id="category-sfizi" className="section">
          <h2>🥨 Sfizi</h2>
          <div className="products-grid">
            {products.sfizi.map(product => (
              <ProductCard key={product.id} product={product} quantity={cart[product.id]?.quantity || 0} ordersEnabled={ordersEnabled} cartQtyForProduct={getCartQtyForProduct(product.id)} onMenuSelect={handleMenuSelection} onPatatineSelect={handlePatatineSelection} onPaniniSelect={handlePaniniSelection} onUpdateQty={updateQuantity} />
            ))}
          </div>
        </div>

        <div id="category-dolci" className="section">
          <h2>🍰 Dolci</h2>
          <div className="products-grid">
            {products.dolci.map(product => (
              <ProductCard key={product.id} product={product} quantity={cart[product.id]?.quantity || 0} ordersEnabled={ordersEnabled} cartQtyForProduct={getCartQtyForProduct(product.id)} onMenuSelect={handleMenuSelection} onPatatineSelect={handlePatatineSelection} onPaniniSelect={handlePaniniSelection} onUpdateQty={updateQuantity} />
            ))}
          </div>
        </div>

        <div id="category-bibite" className="section">
          <h2>🥤 Bibite</h2>
          <div className="products-grid">
            {products.bibite.map(product => (
              <ProductCard key={product.id} product={product} quantity={cart[product.id]?.quantity || 0} ordersEnabled={ordersEnabled} cartQtyForProduct={getCartQtyForProduct(product.id)} onMenuSelect={handleMenuSelection} onPatatineSelect={handlePatatineSelection} onPaniniSelect={handlePaniniSelection} onUpdateQty={updateQuantity} />
            ))}
          </div>
        </div>
      </div>

      {ordersEnabled && getCartItemsCount() > 0 && (
        <div className="cart-footer">
          <div className="cart-info">
            <span className="cart-total">
              Totale: €{getCartTotal().toFixed(2)} ({getCartItemsCount()} articoli)
            </span>
          </div>
          <button 
            className="checkout-button"
            onClick={() => {
              const orderData = {
                cart: cart,
                products: products
              };
              onNavigate('checkout', orderData);
            }}
          >
            Vai al Pagamento
          </button>
        </div>
      )}

      <MenuSelectionModal 
        isOpen={showMenuModal}
        onClose={() => {
          setShowMenuModal(false);
          setSelectedMenu(null);
        }}
        onSelectOptions={handleMenuOptions}
        menuProduct={selectedMenu}
      />

      <PatatineSelectionModal 
        isOpen={showPatatineModal}
        onClose={() => {
          setShowPatatineModal(false);
          setSelectedPatatine(null);
        }}
        onSelectIngredients={handlePatatineOptions}
        patatineProduct={selectedPatatine}
      />

      <PaniniSelectionModal 
        isOpen={showPaniniModal}
        onClose={() => {
          setShowPaniniModal(false);
          setSelectedPanini(null);
        }}
        onSelectSauces={handlePaniniOptions}
        paniniProduct={selectedPanini}
      />
    </div>
  );
}

function CheckoutPage({ onNavigate, checkoutData }) {
  const [customerInfo, setCustomerInfo] = useState({
    car_model: '',
    car_color: '',
    license_plate: '',
    payment_method: 'contanti'
  });
  const [loading, setLoading] = useState(false);
  const [showOrdersDisabledModal, setShowOrdersDisabledModal] = useState(false);
  const [disabledReason, setDisabledReason] = useState('');
  const [serviceHours, setServiceHours] = useState({ start_time: '18:00', end_time: '23:00' });

  // 🔹 prendi prodotti dal dato passato da App (checkoutData)
  const cart = loadCartFromStorage();
  const { products } = checkoutData || { products: {} };

  const SERVICE_FEE = 1.0;

  // Scroll automatico al riepilogo ordine quando la pagina si carica
  useEffect(() => {
    const scrollToOrderSummary = () => {
      const orderSummaryElement = document.querySelector('.order-summary');
      if (orderSummaryElement) {
        orderSummaryElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    };

    const timeoutId = setTimeout(scrollToOrderSummary, 100);
    return () => clearTimeout(timeoutId);
  }, []);

  // --- FIX per mostrare salse/ingredienti con quantità ---
  const formatOptionList = (list) => {
    if (!Array.isArray(list)) return '';

    return list
      .map((item) => {
        // Caso stringa (vecchio formato)
        if (typeof item === 'string') return item;

        // Caso oggetto { name, quantity, price_modifier }
        if (item && typeof item === 'object') {
          const name = item.name || '';
          const qty =
            item.quantity && Number(item.quantity) > 1
              ? ` x${Number(item.quantity)}`
              : '';
          return `${name}${qty}`.trim();
        }

        return '';
      })
      .filter(Boolean)
      .join(', ');
  };


  const getOrderItems = () => {
    const items = [];

    Object.entries(cart).forEach(([cartKey, cartItem]) => {
      if (cartItem && cartItem.quantity && cartItem.product) {
        const product = cartItem.product;

        // Menu personalizzato
        if (cartItem.selectedDrink && cartItem.selectedFries) {
          items.push({
            product_id: product.id,
            product_name: product.name,
            quantity: cartItem.quantity,
            unit_price: cartItem.totalPrice || product.price,
            total_price: (cartItem.totalPrice || product.price) * cartItem.quantity,
            selected_fries: cartItem.selectedFries,
            selected_drink: cartItem.selectedDrink,
            drink_modifier: cartItem.drinkModifier || 0.0,
            fries_modifier: cartItem.friesModifier || 0.0,
            selected_menu_sauces: cartItem.selectedMenuSauces || [],
            menu_sauces_modifier: cartItem.menuSaucesModifier || 0.0,
            selected_ingredients: null,
            selected_sauces: null,
            sauces_modifier: 0.0
          });
        }
        // Patatine personalizzate
        else if (cartItem.selectedIngredients) {
          items.push({
            product_id: product.id,
            product_name: product.name,
            quantity: cartItem.quantity,
            unit_price: cartItem.totalPrice || product.price,
            total_price: (cartItem.totalPrice || product.price) * cartItem.quantity,
            selected_fries: null,
            selected_drink: null,
            drink_modifier: 0.0,
            fries_modifier: 0.0,
            selected_ingredients: cartItem.selectedIngredients,
            selected_sauces: null,
            sauces_modifier: 0.0,
            selected_menu_sauces: null,
            menu_sauces_modifier: 0.0
          });
        }
        // Panini personalizzati
        else if (cartItem.selectedSauces) {
          items.push({
            product_id: product.id,
            product_name: product.name,
            quantity: cartItem.quantity,
            unit_price: cartItem.totalPrice || product.price,
            total_price: (cartItem.totalPrice || product.price) * cartItem.quantity,
            selected_fries: null,
            selected_drink: null,
            drink_modifier: 0.0,
            fries_modifier: 0.0,
            selected_ingredients: null,
            selected_sauces: cartItem.selectedSauces,
            sauces_modifier: cartItem.saucesModifiers || 0.0,
            selected_menu_sauces: null,
            menu_sauces_modifier: 0.0
          });
        }
        // Prodotto normale
        else {
          items.push({
            product_id: product.id,
            product_name: product.name,
            quantity: cartItem.quantity,
            unit_price: product.price,
            total_price: product.price * cartItem.quantity,
            selected_fries: null,
            selected_drink: null,
            drink_modifier: 0.0,
            fries_modifier: 0.0,
            selected_ingredients: null,
            selected_sauces: null,
            sauces_modifier: 0.0,
            selected_menu_sauces: null,
            menu_sauces_modifier: 0.0
          });
        }
      }
    });

    return items;
  };

  const getSubtotal = () => {
    return getOrderItems().reduce((sum, item) => sum + item.total_price, 0);
  };

  const getTotal = () => {
    return getSubtotal() + SERVICE_FEE;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 🔒 controllo stato ordinazioni
      const statusResponse = await fetch(`${BACKEND_URL}/api/settings/status`);
      const statusData = await statusResponse.json();

      if (!statusData.orders_enabled) {
        // ordinazioni disattivate → mostra modal
        try {
          const hoursResponse = await fetch(`${BACKEND_URL}/api/settings/hours`);
          if (hoursResponse.ok) {
            const hoursData = await hoursResponse.json();
            setServiceHours({
              start_time: hoursData.start_time,
              end_time: hoursData.end_time
            });
          }
        } catch (hoursError) {
          console.log('Using default hours for modal');
        }

        setDisabledReason(statusData.reason || 'manually_disabled');
        setShowOrdersDisabledModal(true);
        setLoading(false);
        return;
      }

      // ordinazioni attive → invia ordine
      const orderDataToSend = {
        items: getOrderItems(),
        customer_info: customerInfo,
        subtotal: getSubtotal(),
        service_fee: SERVICE_FEE,
        total_amount: getTotal()
      };

      const response = await fetch(`${BACKEND_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderDataToSend)
      });

      if (response.ok) {
        const result = await response.json();
        clearCartFromStorage();
        onNavigate('confirmation', {
          orderNumber: result.order_number,
          orderId: result.order_id
        });
      } else if (response.status === 409) {
        const err = await response.json();
        alert(`Alcuni prodotti non sono più disponibili:\n\n${err.detail}\n\nModifica il carrello e riprova.`);
      } else {
        console.error('Order creation error:', await response.json());
        alert(`Errore nella creazione dell'ordine. Contattare lo staff.`);
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Errore di connessione. Riprovare.');
    }
    setLoading(false);
  };

  return (
    <div className="checkout-page">
      <div className="checkout-header">
        <button className="back-button" onClick={() => onNavigate('order')}>
          ← Torna al Menu
        </button>
        <h1>Conferma Ordine</h1>
      </div>

      <div className="checkout-content">
        <div className="order-summary">
          <h2>Riepilogo Ordine</h2>
          {getOrderItems().length === 0 ? (
            <div className="empty-cart">
              <p>Il carrello è vuoto</p>
              <button
                className="back-button"
                onClick={() => onNavigate('order')}
              >
                Torna agli Ordini
              </button>
            </div>
          ) : (
            <>
              {getOrderItems().map((item, index) => (
                <div key={index} className="order-item">
                  <div className="item-details">
                    <span>{item.quantity}x {item.product_name}</span>

                    {item.selected_fries && (
                      <small>Patatine: {item.selected_fries}</small>
                    )}

                    {item.selected_drink && (
                      <small>Bibita: {item.selected_drink}</small>
                    )}

                    {item.selected_menu_sauces && item.selected_menu_sauces.length > 0 && (
                      <small>Salse Menu: {formatOptionList(item.selected_menu_sauces)}</small>
                    )}

                    {item.selected_ingredients && item.selected_ingredients.length > 0 && (
                      <small>Extra: {formatOptionList(item.selected_ingredients)}</small>
                    )}

                    {item.selected_sauces && item.selected_sauces.length > 0 && (
                      <small>Salse: {formatOptionList(item.selected_sauces)}</small>
                    )}
                  </div>
                  <span>€{item.total_price.toFixed(2)}</span>
                </div>
              ))}


              <div className="order-item subtotal">
                <span>Subtotale</span>
                <span>€{getSubtotal().toFixed(2)}</span>
              </div>

              <div className="order-item service-fee">
                <span>Servizio in auto</span>
                <span>€{SERVICE_FEE.toFixed(2)}</span>
              </div>

              <div className="order-total">
                <strong>Totale: €{getTotal().toFixed(2)}</strong>
              </div>
            </>
          )}
        </div>

        <form onSubmit={handleSubmit} className="customer-form">
          <h2>Dati Auto</h2>

          <div className="form-group">
            <label>Modello Auto *</label>
            <input
              type="text"
              value={customerInfo.car_model}
              onChange={(e) =>
                setCustomerInfo({ ...customerInfo, car_model: e.target.value })
              }
              placeholder="Es. Fiat 500"
              required
            />
          </div>

          <div className="form-group">
            <label>Colore Auto *</label>
            <input
              type="text"
              value={customerInfo.car_color}
              onChange={(e) =>
                setCustomerInfo({ ...customerInfo, car_color: e.target.value })
              }
              placeholder="Es. Rosso"
              required
            />
          </div>

          <div className="form-group">
            <label>Numero Targa *</label>
            <input
              type="text"
              value={customerInfo.license_plate}
              onChange={(e) =>
                setCustomerInfo({
                  ...customerInfo,
                  license_plate: e.target.value
                })
              }
              placeholder="Es. AB123CD"
              required
            />
          </div>

          <div className="form-group">
            <label>Metodo di Pagamento</label>
            <select
              value={customerInfo.payment_method}
              onChange={(e) =>
                setCustomerInfo({
                  ...customerInfo,
                  payment_method: e.target.value
                })
              }
            >
              <option value="contanti">Contanti</option>
              <option value="carta">Carta</option>
            </select>
          </div>

          <button type="submit" className="confirm-button" disabled={loading}>
            {loading ? 'Elaborazione...' : 'Conferma Ordine'}
          </button>
        </form>
      </div>

      {/* Modal riuso dello stesso OrdersDisabledModal della home */}
      <OrdersDisabledModal
        isOpen={showOrdersDisabledModal}
        onClose={() => setShowOrdersDisabledModal(false)}
        serviceHours={serviceHours}
        disabledReason={disabledReason}
      />
    </div>
  );
}


function ConfirmationPage({ onNavigate, confirmationData }) {
  const { orderNumber, orderId } = confirmationData;

  return (
    <div className="confirmation-page">
      <div className="confirmation-content">
        <div className="success-icon">✅</div>
        <h1>Ordine Confermato!</h1>
        <div className="order-details">
          <p><strong>Numero Ordine: #{orderNumber}</strong></p>
          <p>Il tuo ordine è stato ricevuto e verrà preparato a breve.</p>
          <p>Appena sarà pronto ti verrà portato in auto.</p>
        </div>
        <button className="home-button" onClick={() => onNavigate('home')}>
          Torna alla Home
        </button>
      </div>
    </div>
  );
}

function StaffDashboard({ onLogout }) {
  const [currentView, setCurrentView] = useState('orders');
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState({ total_orders: 0, total_revenue: 0 });
  const [settings, setSettings] = useState({ orders_enabled: true, start_time: '18:00', end_time: '23:00' });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [previousOrdersCount, setPreviousOrdersCount] = useState(0);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const notifiedOrderIds = useRef(new Set()); // useRef per accesso sincrono immediato
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(() => {
    return localStorage.getItem('drive_in_auto_print') === 'true';
  });
  const autoPrintRef = useRef(localStorage.getItem('drive_in_auto_print') === 'true');
  const printedOrderIds = useRef(new Set());

  // === Bluetooth PT-210 State ===
  const [btStatus, setBtStatus] = useState('disconnesso'); // disconnesso | connessione | connesso | errore
  const [btError, setBtError] = useState('');
  const btDeviceRef = useRef(null);
  const btCharRef = useRef(null);
  const [orderPrintStatus, setOrderPrintStatus] = useState({}); // { orderId: 'invio'|'inviato'|'errore' }
  const btPrintQueueRef = useRef([]); // coda stampa serializzata
  const btPrintingRef = useRef(false); // lock per evitare stampe concorrenti

  // UUID della PT-210
  const PT210_SERVICE       = '49535343-fe7d-4ae5-8fa9-9fafd205e455';
  const PT210_WRITE_CHAR    = '49535343-8841-43f4-a8d4-ecbe34729bb3';
  const PT210_SERVICE_ALT   = '0000ff00-0000-1000-8000-00805f9b34fb';
  const PT210_WRITE_CHAR_ALT = '0000ff02-0000-1000-8000-00805f9b34fb';

  // Scrive dati in chunk al dispositivo BLE
  const btWriteChunked = useCallback(async (data) => {
    const char = btCharRef.current;
    if (!char) throw new Error('Caratteristica BLE non disponibile');
    const CHUNK = 100;
    const useWithResponse = char.properties.write;
    for (let offset = 0; offset < data.length; offset += CHUNK) {
      const chunk = data.slice(offset, offset + CHUNK);
      if (useWithResponse) {
        await char.writeValueWithResponse(chunk);
      } else {
        await char.writeValueWithoutResponse(chunk);
      }
      if (offset + CHUNK < data.length) {
        await new Promise(r => setTimeout(r, 15));
      }
    }
  }, []);

  // Connessione alla PT-210
  const btConnect = useCallback(async (useAllDevices = false) => {
    setBtError('');
    setBtStatus('connessione');
    try {
      if (!navigator.bluetooth) {
        throw new Error('Web Bluetooth non disponibile. Su iPhone usa Safari con beacio installato.');
      }

      const requestOpts = useAllDevices
        ? { acceptAllDevices: true, optionalServices: [PT210_SERVICE, PT210_SERVICE_ALT] }
        : { filters: [{ namePrefix: 'PT210' }], optionalServices: [PT210_SERVICE, PT210_SERVICE_ALT] };

      const device = await navigator.bluetooth.requestDevice(requestOpts);

      device.addEventListener('gattserverdisconnected', () => {
        console.log('🔴 BT DISCONNESSO: evento gattserverdisconnected ricevuto');
        btCharRef.current = null;
        setBtStatus('disconnesso');
        setBtError('Stampante disconnessa');
      });

      const server = await device.gatt.connect();
      btDeviceRef.current = device;

      // Prova il servizio primario, poi l'alternativo
      let writeChar = null;
      const configs = [
        { svc: PT210_SERVICE, chr: PT210_WRITE_CHAR },
        { svc: PT210_SERVICE_ALT, chr: PT210_WRITE_CHAR_ALT },
      ];

      for (const cfg of configs) {
        try {
          const service = await server.getPrimaryService(cfg.svc);
          writeChar = await service.getCharacteristic(cfg.chr);
          break;
        } catch (_) { /* prova il prossimo */ }
      }

      if (!writeChar) {
        // Fallback: cerca qualsiasi caratteristica scrivibile
        for (const cfg of configs) {
          try {
            const service = await server.getPrimaryService(cfg.svc);
            const chars = await service.getCharacteristics();
            writeChar = chars.find(c => c.properties.write || c.properties.writeWithoutResponse);
            if (writeChar) break;
          } catch (_) { /* continua */ }
        }
      }

      if (!writeChar) {
        throw new Error('Caratteristica di scrittura non trovata. Verifica che la stampante sia accesa.');
      }

      btCharRef.current = writeChar;
      setBtStatus('connesso');
      console.log('🔵 BT CONNESSO: caratteristica GATT disponibile per la stampa');
    } catch (err) {
      btCharRef.current = null;
      if (err.name === 'NotFoundError' || err.message?.includes('cancelled')) {
        setBtStatus('disconnesso');
        setBtError('Selezione annullata');
      } else {
        setBtStatus('errore');
        setBtError(err.message || 'Errore di connessione');
      }
    }
  }, []);

  // Disconnessione
  const btDisconnect = useCallback(() => {
    if (btDeviceRef.current?.gatt?.connected) {
      btDeviceRef.current.gatt.disconnect();
    }
    btCharRef.current = null;
    btDeviceRef.current = null;
    setBtStatus('disconnesso');
    setBtError('');
  }, []);

  // Stampa di prova
  const btTestPrint = useCallback(async () => {
    try {
      const encoder = new TextEncoder();
      const ESC = 0x1B;
      const GS = 0x1D;
      const LF = 0x0A;

      // Init
      const init = new Uint8Array([ESC, 0x40]);
      // Allinea al centro
      const center = new Uint8Array([ESC, 0x61, 0x01]);
      // Testo doppia altezza+larghezza
      const bigText = new Uint8Array([GS, 0x21, 0x11]);
      // Testo normale
      const normalText = new Uint8Array([GS, 0x21, 0x00]);
      // Bold on/off
      const boldOn = new Uint8Array([ESC, 0x45, 0x01]);
      const boldOff = new Uint8Array([ESC, 0x45, 0x00]);

      const line1 = encoder.encode('DRIVE IN POZZUOLI');
      const line2 = encoder.encode('TEST STAMPA PT-210');
      const line3 = encoder.encode('CONNESSIONE OK');
      const feeds = new Uint8Array([LF, LF, LF]);

      // Componi il buffer
      const parts = [init, center, bigText, boldOn, line1, new Uint8Array([LF]), normalText, boldOff, new Uint8Array([LF]), line2, new Uint8Array([LF]), line3, new Uint8Array([LF]), feeds];
      const totalLen = parts.reduce((s, p) => s + p.length, 0);
      const buffer = new Uint8Array(totalLen);
      let pos = 0;
      for (const p of parts) {
        buffer.set(p, pos);
        pos += p.length;
      }

      await btWriteChunked(buffer);
      return true;
    } catch (err) {
      setBtError('Stampa fallita: ' + (err.message || err));
      return false;
    }
  }, [btWriteChunked]);

  // === Generatore ESC/POS Comanda ===
  const buildEscPosComanda = useCallback((order) => {
    const ESC = 0x1B, GS = 0x1D, LF = 0x0A;

    const sanitize = (text) => {
      if (!text) return '';
      return text
        .replace(/€/g, 'EUR')
        .replace(/à/g, 'a').replace(/è/g, 'e').replace(/é/g, 'e')
        .replace(/ì/g, 'i').replace(/ò/g, 'o').replace(/ù/g, 'u')
        .replace(/À/g, 'A').replace(/È/g, 'E').replace(/É/g, 'E')
        .replace(/Ì/g, 'I').replace(/Ò/g, 'O').replace(/Ù/g, 'U');
    };

    const formatOption = (list) => {
      if (!Array.isArray(list) || list.length === 0) return '';
      return list.map(item => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const qty = item.quantity && Number(item.quantity) > 1 ? ` x${item.quantity}` : '';
          return `${item.name || ''}${qty}`.trim();
        }
        return '';
      }).filter(Boolean).join(', ');
    };

    const wrapText = (text, maxChars) => {
      const lines = [];
      const words = text.split(' ');
      let line = '';
      for (const w of words) {
        if (line.length + w.length + (line ? 1 : 0) > maxChars) {
          if (line) lines.push(line);
          if (w.length > maxChars) {
            for (let i = 0; i < w.length; i += maxChars) lines.push(w.substring(i, i + maxChars));
            line = '';
          } else { line = w; }
        } else { line = line ? line + ' ' + w : w; }
      }
      if (line) lines.push(line);
      return lines;
    };

    const enc = new TextEncoder();
    const parts = [];
    const push = (data) => parts.push(data instanceof Uint8Array ? data : enc.encode(sanitize(String(data))));
    const lf = () => push(new Uint8Array([LF]));
    const cmd = (...bytes) => push(new Uint8Array(bytes));

    // INIT
    cmd(ESC, 0x40);

    // INTESTAZIONE
    cmd(ESC, 0x61, 0x01); // centro
    cmd(ESC, 0x45, 0x01); // bold on
    push('DRIVE IN POZZUOLI'); lf();
    cmd(ESC, 0x45, 0x00);
    push('--------------------------------'); lf();

    // NUMERO ORDINE (grande)
    cmd(ESC, 0x61, 0x01);
    cmd(GS, 0x21, 0x11); // doppia altezza + larghezza
    cmd(ESC, 0x45, 0x01);
    push(`#${order.order_number}`); lf();
    cmd(GS, 0x21, 0x00);
    cmd(ESC, 0x45, 0x00);

    // ORARIO
    const orderTime = (() => {
      const utcDate = new Date(order.created_at + 'Z');
      return utcDate.toLocaleTimeString('it-IT', { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    })();
    push(orderTime); lf();
    push('--------------------------------'); lf();

    // DATI CLIENTE
    cmd(ESC, 0x61, 0x00);
    cmd(ESC, 0x45, 0x01);
    push(`${order.customer_info.car_model || ''} ${order.customer_info.car_color || ''}`.trim()); lf();
    cmd(ESC, 0x45, 0x00);
    if (order.customer_info.license_plate) { push(`Targa: ${order.customer_info.license_plate}`); lf(); }
    if (order.customer_info.notes) { push(`Note: ${order.customer_info.notes}`); lf(); }
    push('--------------------------------'); lf();

    // PRODOTTI
    order.items.forEach((item) => {
      cmd(ESC, 0x61, 0x00);
      cmd(GS, 0x21, 0x01); // doppia altezza
      cmd(ESC, 0x45, 0x01);
      const hdr = `${item.quantity}x ${item.product_name}`;
      wrapText(hdr, 32).forEach(l => { push(l); lf(); });
      cmd(GS, 0x21, 0x00);
      cmd(ESC, 0x45, 0x00);

      const details = [];
      if (item.selected_fries) details.push(`  PAT: ${item.selected_fries}`);
      if (item.selected_drink) details.push(`  BIB: ${item.selected_drink}`);
      if (item.selected_menu_sauces?.length) details.push(`  SALSE: ${formatOption(item.selected_menu_sauces)}`);
      if (item.selected_ingredients?.length) details.push(`  EXTRA: ${formatOption(item.selected_ingredients)}`);
      if (item.selected_sauces?.length) details.push(`  SALSE: ${formatOption(item.selected_sauces)}`);
      details.forEach(d => wrapText(d, 32).forEach(l => { push(l); lf(); }));
      lf();
    });

    // TOTALE
    push('================================'); lf();
    cmd(GS, 0x21, 0x11);
    cmd(ESC, 0x45, 0x01);
    cmd(ESC, 0x61, 0x02); // destra
    push(`EUR ${order.total_amount.toFixed(2)}`); lf();
    cmd(GS, 0x21, 0x00);
    cmd(ESC, 0x45, 0x00);

    // PAGAMENTO
    cmd(ESC, 0x61, 0x01);
    cmd(ESC, 0x45, 0x01);
    push(order.customer_info.payment_method === 'contanti' ? 'CONTANTI' : 'CARTA'); lf();
    cmd(ESC, 0x45, 0x00);

    // 3 avanzamenti riga
    cmd(ESC, 0x64, 0x03);

    const totalLen = parts.reduce((s, p) => s + p.length, 0);
    const buffer = new Uint8Array(totalLen);
    let pos = 0;
    for (const p of parts) { buffer.set(p, pos); pos += p.length; }
    return buffer;
  }, []);

  // === Coda di stampa serializzata ===
  const processQueue = useCallback(async () => {
    if (btPrintingRef.current) return;
    if (btPrintQueueRef.current.length === 0) return;
    if (!btCharRef.current) return;

    btPrintingRef.current = true;
    while (btPrintQueueRef.current.length > 0) {
      const { orderId, data, resolve } = btPrintQueueRef.current[0];
      setOrderPrintStatus(prev => ({ ...prev, [orderId]: 'invio' }));
      try {
        await btWriteChunked(data);
        setOrderPrintStatus(prev => ({ ...prev, [orderId]: 'inviato' }));
        resolve(true);
      } catch (err) {
        setOrderPrintStatus(prev => ({ ...prev, [orderId]: 'errore' }));
        resolve(false);
      }
      btPrintQueueRef.current.shift();
      if (btPrintQueueRef.current.length > 0) {
        await new Promise(r => setTimeout(r, 500));
      }
    }
    btPrintingRef.current = false;
  }, [btWriteChunked]);

  // === Stampa BT ordine ===
  const btPrintOrder = useCallback(async (order) => {
    if (!btCharRef.current) {
      setBtError('Stampante non connessa. Collega la PT-210 dalle Impostazioni.');
      return false;
    }
    const data = buildEscPosComanda(order);
    return new Promise((resolve) => {
      btPrintQueueRef.current.push({ orderId: order.id, data, resolve });
      processQueue();
    });
  }, [buildEscPosComanda, processQueue]);

  // === Stampa tradizionale (vecchio sistema browser) ===
  const printTraditional = useCallback((order) => {
    const formatOption = (list) => {
      if (!Array.isArray(list) || list.length === 0) return '';
      return list.map(item => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const qty = item.quantity && Number(item.quantity) > 1 ? ` x${item.quantity}` : '';
          return `${item.name || ''}${qty}`.trim();
        }
        return '';
      }).filter(Boolean).join(', ');
    };

    const orderTime = (() => {
      const utcDate = new Date(order.created_at + 'Z');
      return utcDate.toLocaleTimeString('it-IT', {
        timeZone: 'Europe/Rome',
        hour: '2-digit',
        minute: '2-digit',
      });
    })();

    let itemsHTML = '';
    order.items.forEach((item, idx) => {
      // Riga principale articolo: quantità grande + nome
      itemsHTML += `<div class="item">
        <div class="item-main">
          <span class="item-qty">${item.quantity}x</span>
          <span class="item-name">${item.product_name}</span>
        </div>`;

      // Dettagli personalizzazione ben visibili
      let details = [];
      if (item.selected_fries) details.push(`PATATINE: ${item.selected_fries}`);
      if (item.selected_drink) details.push(`BIBITA: ${item.selected_drink}`);
      if (item.selected_menu_sauces?.length) details.push(`SALSE MENU: ${formatOption(item.selected_menu_sauces)}`);
      if (item.selected_ingredients?.length) details.push(`EXTRA: ${formatOption(item.selected_ingredients)}`);
      if (item.selected_sauces?.length) details.push(`SALSE: ${formatOption(item.selected_sauces)}`);

      if (details.length > 0) {
        itemsHTML += `<div class="item-details">`;
        details.forEach(d => {
          itemsHTML += `<div class="detail-line">&gt; ${d}</div>`;
        });
        itemsHTML += `</div>`;
      }
      itemsHTML += `</div>`;
    });

    const comandaHTML = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Comanda #${order.order_number}</title>
<style>
  @page { margin: 2mm; size: 58mm auto; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Courier New', monospace; width: 58mm; max-width: 58mm; padding: 2mm; color: #000; }
  .title { text-align: center; font-size: 14px; font-weight: bold; letter-spacing: 2px; border-bottom: 2px solid #000; padding-bottom: 3px; margin-bottom: 4px; }
  .order-num { text-align: center; font-size: 32px; font-weight: bold; line-height: 1.1; margin: 6px 0 2px; }
  .order-time { text-align: center; font-size: 14px; margin-bottom: 4px; padding-bottom: 4px; border-bottom: 2px solid #000; }
  .sep { border-bottom: 1px dashed #000; margin: 4px 0; }
  .section-label { font-size: 10px; font-weight: bold; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 2px; }
  .customer { padding: 4px 0; font-size: 12px; line-height: 1.4; }
  .customer .car { font-size: 14px; font-weight: bold; }
  .items { padding: 4px 0; }
  .item { margin-bottom: 6px; }
  .item-main { display: flex; align-items: baseline; gap: 4px; }
  .item-qty { font-size: 18px; font-weight: bold; min-width: 28px; }
  .item-name { font-size: 16px; font-weight: bold; }
  .item-details { padding-left: 6px; margin-top: 1px; }
  .detail-line { font-size: 12px; font-weight: bold; letter-spacing: 0.5px; }
  .total-section { border-top: 2px solid #000; padding-top: 4px; margin-top: 4px; }
  .total-line { display: flex; justify-content: space-between; font-size: 14px; font-weight: bold; }
  .payment { text-align: center; font-size: 14px; font-weight: bold; margin-top: 4px; padding: 3px; border: 1px solid #000; }
  @media print { body { width: 58mm; } }
</style></head>
<body>
  <div class="title">*** COMANDA ***</div>
  <div class="order-num">#${order.order_number}</div>
  <div class="order-time">${orderTime}</div>

  <div class="section-label">CLIENTE / AUTO</div>
  <div class="customer">
    <div class="car">${order.customer_info.car_model} ${order.customer_info.car_color}</div>
    <div>Targa: <strong>${order.customer_info.license_plate}</strong></div>
  </div>
  <div class="sep"></div>

  <div class="section-label">ARTICOLI</div>
  <div class="items">${itemsHTML}</div>

  <div class="total-section">
    <div class="total-line">
      <span>TOTALE</span>
      <span>&euro;${order.total_amount.toFixed(2)}</span>
    </div>
  </div>
  <div class="payment">${order.customer_info.payment_method === 'contanti' ? 'CONTANTI' : 'CARTA'}</div>

  <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();}}</script>
</body></html>`;

    const printWindow = window.open('', '_blank', 'width=240,height=600');
    if (printWindow) {
      printWindow.document.write(comandaHTML);
      printWindow.document.close();
    }
  }, []);

  // Cache per evitare richieste ridondanti
  const [dataCache, setDataCache] = useState({
    products: { data: [], timestamp: 0 },
    stats: { data: null, timestamp: 0 },
    settings: { data: null, timestamp: 0 },
    history: { data: [], timestamp: 0 }
  });
  const CACHE_DURATION = 300000; // 5 minuti di cache

  const toggleAutoPrint = useCallback((enabled) => {
    setAutoPrintEnabled(enabled);
    autoPrintRef.current = enabled;
    localStorage.setItem('drive_in_auto_print', enabled ? 'true' : 'false');
  }, []);

  useEffect(() => {
    fetchData();
    
    // 🚀 POLLING REAL-TIME: Aggiornamenti automatici ogni 3 secondi
    let pollTimer = null;
    let lastOrderCount = 0;
    let lastStatsHash = null;
    
    const startRealTimePolling = () => {
      pollTimer = setInterval(async () => {
        try {
          const token = localStorage.getItem('staff_token');
          const headers = { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          };

          // 🔄 POLLING REAL-TIME: ordini + stats ogni 3 secondi
          const [ordersRes, statsRes] = await Promise.all([
            fetch(`${BACKEND_URL}/api/staff/orders`, { headers }),
            fetch(`${BACKEND_URL}/api/staff/stats`, { headers })
          ]);
          
          let hasUpdates = false;
          
          if (ordersRes.ok) {
            const newOrders = await ordersRes.json();
            
            // Rileva cambiamenti negli ordini
            if (newOrders.length !== lastOrderCount) {
              console.log(`🔔 ORDINI AGGIORNATI: ${newOrders.length} (era ${lastOrderCount})`);
              
              setOrders(newOrders);
              setPreviousOrdersCount(newOrders.length);
              hasUpdates = true;
              
              // Pulisci il Set: rimuovi ordini che non sono più "in_arrivo"
              const currentInArrivoIds = new Set(
                newOrders.filter(o => o.status === 'in_arrivo').map(o => o.id)
              );
              
              // Mantieni solo gli ID che sono ancora "in_arrivo"
              const idsToKeep = new Set();
              notifiedOrderIds.current.forEach(id => {
                if (currentInArrivoIds.has(id)) {
                  idsToKeep.add(id);
                }
              });
              notifiedOrderIds.current = idsToKeep;
              
              // Suono solo per nuovi ordini (incremento)
              if (newOrders.length > lastOrderCount) {
                // Trova ordini "in_arrivo" che non sono stati ancora notificati
                const newInArrivoOrders = newOrders.filter(order => 
                  order.status === 'in_arrivo' && !notifiedOrderIds.current.has(order.id)
                );
                
                if (newInArrivoOrders.length > 0) {
                  console.log(`🔊 RILEVATI ${newInArrivoOrders.length} NUOVI ORDINI DA NOTIFICARE: ${newInArrivoOrders.map(o => o.id).join(', ')}`);
                  
                  // Aggiungi PRIMA gli ID al Set per evitare duplicati nei prossimi cicli
                  newInArrivoOrders.forEach(order => {
                    notifiedOrderIds.current.add(order.id);
                  });
                  
                  // Suona UNA SOLA VOLTA
                  playNotificationSound();
                  
                  // Invia notifica browser
                  sendBrowserNotification(
                    `🔔 ${newInArrivoOrders.length} ${newInArrivoOrders.length === 1 ? 'Nuovo Ordine' : 'Nuovi Ordini'}!`,
                    {
                      body: `Hai ${newOrders.filter(o => o.status === 'in_arrivo').length} ordini in arrivo da gestire`,
                      tag: 'new-order',
                      renotify: true
                    }
                  );
                  
                  console.log(`✅ NOTIFICA INVIATA - Set aggiornato. Totale ordini notificati: ${notifiedOrderIds.current.size}`);
                  
                  // Auto-stampa nuovi ordini via BT se abilitata
                  console.log(`🖨️ AUTO-PRINT CHECK: abilitata=${autoPrintRef.current}, btChar=${!!btCharRef.current}, nuoviOrdini=${newInArrivoOrders.length}`);
                  if (autoPrintRef.current) {
                    if (btCharRef.current) {
                      newInArrivoOrders.forEach(order => {
                        if (!printedOrderIds.current.has(order.id)) {
                          printedOrderIds.current.add(order.id);
                          console.log(`🖨️ AUTO-PRINT: invio ordine #${order.order_number} (${order.id}) alla coda BT`);
                          btPrintOrder(order).then(ok => {
                            console.log(`🖨️ AUTO-PRINT: ordine #${order.order_number} ${ok ? 'inviato con successo' : 'ERRORE invio'}`);
                          });
                        } else {
                          console.log(`🖨️ AUTO-PRINT: ordine #${order.order_number} già stampato, skip`);
                        }
                      });
                    } else {
                      console.log(`🖨️ AUTO-PRINT: stampante BT non connessa, stampa automatica saltata`);
                    }
                  } else {
                    console.log(`🖨️ AUTO-PRINT: disabilitata nelle impostazioni`);
                  }
                } else {
                  console.log(`📝 Nessun nuovo ordine da notificare. Ordini già notificati: ${Array.from(notifiedOrderIds.current).join(', ')}`);
                }
              }
              
              lastOrderCount = newOrders.length;
            }
          }

          if (statsRes.ok) {
            const statsData = await statsRes.json();
            const statsHash = `${statsData.total_orders}-${statsData.total_revenue}`;
            
            // Rileva cambiamenti nelle statistiche
            if (statsHash !== lastStatsHash) {
              console.log(`💰 STATS AGGIORNATE: Ordini ${statsData.total_orders}, Incassi €${statsData.total_revenue}`);
              
              setStats(statsData);
              hasUpdates = true;
              lastStatsHash = statsHash;
            }
          }
          
          if (hasUpdates) {
            console.log(`✅ DASHBOARD AGGIORNATA automaticamente - ${new Date().toLocaleTimeString()}`);
          }
          
        } catch (error) {
          console.error('❌ Errore polling real-time:', error);
        }
      }, 3000); // ⚡ OGNI 3 SECONDI per vero real-time
    };
    
    // Inizializza contatori dopo il primo caricamento
    setTimeout(() => {
      if (orders.length >= 0 && stats.total_orders >= 0) {
        lastOrderCount = orders.length;
        lastStatsHash = `${stats.total_orders}-${stats.total_revenue}`;
        startRealTimePolling();
        console.log(`🚀 POLLING REAL-TIME AVVIATO:`);
        console.log(`   📊 Ordini iniziali: ${lastOrderCount}`);
        console.log(`   💰 Stats iniziali: Ordini ${stats.total_orders}, Incassi €${stats.total_revenue}`);
        console.log(`   ⏱️  Frequenza: ogni 3 secondi`);
      }
    }, 2000);
    
    // Pulizia
    return () => {
      if (pollTimer) {
        clearInterval(pollTimer);
        console.log('🛑 Polling real-time fermato');
      }
    };
  }, []);

  // Calcola incassi separati per contanti e carta
  const calculatePaymentTotals = () => {
    let contanti = 0;
    let carta = 0;
    
    orders.forEach(order => {
      if (order.customer_info.payment_method === 'contanti') {
        contanti += order.total_amount;
      } else if (order.customer_info.payment_method === 'carta') {
        carta += order.total_amount;
      }
    });
    
    return { contanti, carta };
  };
  
  const paymentTotals = calculatePaymentTotals();

  // Funzione per caricare dati specifici per view
  const loadViewData = async (viewName) => {
    try {
      const token = localStorage.getItem('staff_token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const now = Date.now();

      switch (viewName) {
        case 'products':
          if (!dataCache.products.data.length || (now - dataCache.products.timestamp) > CACHE_DURATION) {
            console.log('🔄 Caricamento prodotti...');
            const productsRes = await fetch(`${BACKEND_URL}/api/staff/products`, { headers });
            if (productsRes.ok) {
              const productsData = await productsRes.json();
              setProducts(productsData);
              setDataCache(prev => ({
                ...prev,
                products: { data: productsData, timestamp: now }
              }));
              console.log(`✅ Prodotti caricati: ${productsData.length} prodotti`);
            }
          } else {
            setProducts(dataCache.products.data);
            console.log(`✅ Prodotti dalla cache: ${dataCache.products.data.length} prodotti`);
          }
          break;

        case 'history':
          if (!dataCache.history.data.length || (now - dataCache.history.timestamp) > CACHE_DURATION) {
            console.log('🔄 Caricamento storico...');
            const historyRes = await fetch(`${BACKEND_URL}/api/staff/history`, { headers });
            if (historyRes.ok) {
              const historyData = await historyRes.json();
              setHistory(historyData);
              setDataCache(prev => ({
                ...prev,
                history: { data: historyData, timestamp: now }
              }));
              console.log(`✅ Storico caricato: ${historyData.length} record`);
            }
          } else {
            setHistory(dataCache.history.data);
            console.log(`✅ Storico dalla cache: ${dataCache.history.data.length} record`);
          }
          break;

        case 'settings':
          // Sempre ricarica le impostazioni per essere sicuri di avere dati aggiornati
          console.log('🔄 Caricamento/ricaricamento impostazioni...');
          const settingsRes = await fetch(`${BACKEND_URL}/api/staff/settings`, { headers });
          if (settingsRes.ok) {
            const settingsData = await settingsRes.json();
            setSettings(settingsData);
            setDataCache(prev => ({
              ...prev,
              settings: { data: settingsData, timestamp: now }
            }));
            console.log(`✅ Impostazioni caricate/aggiornate: ${settingsData.start_time} - ${settingsData.end_time}, Ordinazioni: ${settingsData.orders_enabled}`);
          } else {
            console.error('❌ Errore caricamento impostazioni');
          }
          break;
      }
    } catch (error) {
      console.error(`❌ Errore caricamento ${viewName}:`, error);
    }
  };

  // Funzione per riprodurre il suono di notifica
  const playNotificationSound = () => {
    if (!audioEnabled) return;
    
    try {
      // Creiamo un suono di notifica usando Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Creazione di un beep sound
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Configurazione del suono: 800Hz per 0.3 secondi
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.type = 'sine';
      
      // Fade in/out per evitare click
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      
      // Secondo beep dopo una pausa
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();
        
        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);
        
        oscillator2.frequency.setValueAtTime(1000, audioContext.currentTime);
        oscillator2.type = 'sine';
        
        gainNode2.gain.setValueAtTime(0, audioContext.currentTime);
        gainNode2.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.1);
        gainNode2.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
        
        oscillator2.start(audioContext.currentTime);
        oscillator2.stop(audioContext.currentTime + 0.3);
      }, 400);
      
    } catch (error) {
      console.log('Audio not supported or disabled:', error);
    }
  };


  // Funzione per richiedere i permessi delle notifiche browser
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      console.log('❌ Le notifiche non sono supportate da questo browser');
      alert('Il tuo browser non supporta le notifiche');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        console.log('✅ Permesso notifiche concesso');
        // Salva la preferenza
        localStorage.setItem('notifications_enabled', 'true');
        return true;
      } else {
        console.log('❌ Permesso notifiche negato');
        setNotificationsEnabled(false);
        localStorage.setItem('notifications_enabled', 'false');
        return false;
      }
    } catch (error) {
      console.error('Errore richiesta permessi notifiche:', error);
      return false;
    }
  };

  // Funzione per inviare una notifica browser
  const sendBrowserNotification = (title, options = {}) => {
    if (!notificationsEnabled || notificationPermission !== 'granted') {
      return;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico', // Icona dell'app
        badge: '/favicon.ico',
        vibrate: [200, 100, 200], // Vibrazione per mobile
        requireInteraction: true, // La notifica resta finché non viene cliccata
        ...options
      });

      // Click sulla notifica porta il focus alla finestra
      notification.onclick = () => {
        window.focus();
        notification.close();
      };

      // Auto-chiudi dopo 10 secondi
      setTimeout(() => notification.close(), 10000);
      
      console.log('📬 Notifica browser inviata:', title);
    } catch (error) {
      console.error('Errore invio notifica:', error);
    }
  };


  // ============================================
  // PUSH NOTIFICATIONS (Service Worker)
  // ============================================
  
  const [pushEnabled, setPushEnabled] = useState(false);
  const [serviceWorkerRegistration, setServiceWorkerRegistration] = useState(null);

  // Registra Service Worker
  const registerServiceWorker = async () => {
    if (!('serviceWorker' in navigator)) {
      console.log('❌ Service Worker non supportato');
      return null;
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('✅ Service Worker registrato:', registration);
      setServiceWorkerRegistration(registration);
      return registration;
    } catch (error) {
      console.error('❌ Errore registrazione Service Worker:', error);
      return null;
    }
  };

  // Converti chiave pubblica VAPID da base64 a Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Subscribe alle push notifications
  const subscribeToPush = async () => {
    try {
      // Verifica supporto Service Worker
      if (!('serviceWorker' in navigator)) {
        alert('❌ Il tuo browser non supporta i Service Workers.\n\nProva con Chrome, Firefox, Safari o Edge aggiornati.');
        return false;
      }

      // Verifica supporto Notification API
      if (!('Notification' in window)) {
        alert('❌ Il tuo browser non supporta le notifiche.\n\nAggiorna il browser o prova con Chrome/Firefox.');
        return false;
      }

      // Verifica supporto Push API
      if (!('PushManager' in window)) {
        alert('❌ Il tuo browser non supporta le Push Notifications.\n\nProva con Chrome, Firefox o Edge.');
        return false;
      }

      // Registra service worker se non già registrato
      let registration = serviceWorkerRegistration;
      if (!registration) {
        registration = await registerServiceWorker();
        if (!registration) {
          alert('❌ Impossibile registrare il Service Worker.\n\nVerifica di essere su HTTPS.');
          return false;
        }
      }

      // Richiedi permesso notifiche
      let permission = Notification.permission;
      
      if (permission === 'denied') {
        alert('❌ Hai bloccato le notifiche.\n\nVai nelle impostazioni del browser e riattivale per questo sito.');
        return false;
      }

      if (permission !== 'granted') {
        permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          alert('⚠️ Permesso notifiche negato.\n\nLe push notifications non funzioneranno.');
          return false;
        }
      }

      // Ottieni chiave pubblica VAPID dal backend
      const token = localStorage.getItem('staff_token');
      if (!token) {
        alert('❌ Sessione scaduta. Effettua nuovamente il login.');
        return false;
      }

      const vapidRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/push/vapid-public-key`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!vapidRes.ok) {
        const error = await vapidRes.text();
        console.error('Errore ottenimento VAPID key:', error);
        alert('❌ Errore configurazione server. Contatta l\'amministratore.');
        return false;
      }

      const { public_key } = await vapidRes.json();
      
      if (!public_key) {
        alert('❌ VAPID key non configurata sul server. Contatta l\'amministratore.');
        return false;
      }

      const applicationServerKey = urlBase64ToUint8Array(public_key);

      // Subscribe
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });

      console.log('✅ Push subscription ottenuta:', subscription);

      // Salva subscription nel backend
      const subscribeRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/push/subscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });

      if (subscribeRes.ok) {
        setPushEnabled(true);
        localStorage.setItem('push_enabled', 'true');
        console.log('✅ Push notifications attivate!');
        
        // Notifica di test (solo notifica browser normale)
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('📱 Push Attivate!', {
            body: 'Riceverai notifiche anche con telefono in standby!',
            icon: '/favicon.ico'
          });
        }
        
        return true;
      } else {
        const error = await subscribeRes.text();
        console.error('Errore salvataggio subscription:', error);
        alert('❌ Errore salvataggio subscription sul server.');
        return false;
      }

    } catch (error) {
      console.error('❌ Errore attivazione push:', error);
      
      let message = 'Errore attivazione push notifications:\n\n';
      
      if (error.message.includes('not supported')) {
        message += 'Il tuo browser non supporta questa funzionalità.\n\nProva con Chrome, Firefox o Edge aggiornati.';
      } else if (error.message.includes('HTTPS')) {
        message += 'Le push notifications richiedono una connessione sicura (HTTPS).';
      } else if (error.message.includes('permission')) {
        message += 'Permesso notifiche negato.\n\nVai nelle impostazioni del browser per abilitarle.';
      } else {
        message += error.message;
      }
      
      alert(message);
      return false;
    }
  };

  // Unsubscribe dalle push notifications
  const unsubscribeFromPush = async () => {
    try {
      if (!serviceWorkerRegistration) {
        setPushEnabled(false);
        localStorage.setItem('push_enabled', 'false');
        return true;
      }

      const subscription = await serviceWorkerRegistration.pushManager.getSubscription();
      if (subscription) {
        // Rimuovi subscription dal backend
        const token = localStorage.getItem('staff_token');
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            subscription: subscription.toJSON()
          })
        });

        // Unsubscribe dal browser
        await subscription.unsubscribe();
        console.log('✅ Push notifications disattivate');
      }

      setPushEnabled(false);
      localStorage.setItem('push_enabled', 'false');
      return true;

    } catch (error) {
      console.error('❌ Errore disattivazione push:', error);
      return false;
    }
  };

  // Controlla se push sono già attive al mount
  useEffect(() => {
    const checkPushStatus = async () => {
      const savedPref = localStorage.getItem('push_enabled');
      if (savedPref === 'true' && 'serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          setServiceWorkerRegistration(registration);
          
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            setPushEnabled(true);
            console.log('✅ Push notifications già attive');
          } else {
            localStorage.setItem('push_enabled', 'false');
          }
        } catch (error) {
          console.error('Errore controllo push status:', error);
        }
      }
    };

    checkPushStatus();
  }, []);


  // Carica preferenze notifiche al mount
  useEffect(() => {
    const savedPref = localStorage.getItem('notifications_enabled');
    if (savedPref === 'true' && 'Notification' in window) {
      setNotificationsEnabled(true);
      setNotificationPermission(Notification.permission);
    }
  }, []);


  const fetchData = async () => {
    try {
      const token = localStorage.getItem('staff_token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // Carica sempre ordini e stats insieme per aggiornare dashboard
      const [ordersRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/staff/orders`, { headers }),
        fetch(`${BACKEND_URL}/api/staff/stats`, { headers })
      ]);
      
      if (ordersRes.ok) {
        const newOrders = await ordersRes.json();
        
        // Controlla nuovi ordini per audio (solo se non è il caricamento iniziale)
        if (!loading && newOrders.length > previousOrdersCount) {
          const newInArrivoOrders = newOrders.filter(order => order.status === 'in_arrivo');
          const previousInArrivoCount = orders.filter(order => order.status === 'in_arrivo').length;
          
          if (newInArrivoOrders.length > previousInArrivoCount) {
            playNotificationSound();
            console.log(`🔔 Nuovo ordine ricevuto! Totale ordini in arrivo: ${newInArrivoOrders.length}`);
          }
        }
        
        setOrders(newOrders);
        setPreviousOrdersCount(newOrders.length);
        console.log(`✅ Ordini caricati: ${newOrders.length} ordini`);
      }

      // Aggiorna sempre le statistiche (ordini oggi, incassi)
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
        console.log(`✅ Stats aggiornate - Ordini oggi: ${statsData.total_orders}, Incassi: €${statsData.total_revenue}`);
      }

      // Carica sempre le impostazioni di base per avere orari disponibili
      try {
        const settingsRes = await fetch(`${BACKEND_URL}/api/staff/settings`, { headers });
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setSettings(settingsData);
          console.log(`✅ Impostazioni caricate: orari ${settingsData.start_time} - ${settingsData.end_time}`);
        }
      } catch (settingsError) {
        console.error('Errore caricamento impostazioni iniziali:', settingsError);
      }

      // OTTIMIZZAZIONE: Non caricare più altri dati automaticamente
      // I dati vengono caricati solo quando l'utente clicca sulla sezione specifica

    } catch (error) {
      console.error('Error fetching primary data:', error);
    }
    setLoading(false);
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const token = localStorage.getItem('staff_token');
      const response = await fetch(`${BACKEND_URL}/api/staff/orders/${orderId}/status`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const toggleOrdersEnabled = async () => {
    try {
      const token = localStorage.getItem('staff_token');
      const newSettings = { ...settings, orders_enabled: !settings.orders_enabled };
      
      const response = await fetch(`${BACKEND_URL}/api/staff/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        setSettings(newSettings);
      }
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const updateTimeSettings = async (newSettings) => {
    try {
      const token = localStorage.getItem('staff_token');
      
      const response = await fetch(`${BACKEND_URL}/api/staff/settings`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newSettings)
      });

      if (response.ok) {
        setSettings(newSettings);
      }
    } catch (error) {
      console.error('Error updating time settings:', error);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo prodotto?')) return;

    try {
      const token = localStorage.getItem('staff_token');
      const response = await fetch(`${BACKEND_URL}/api/staff/products/${productId}`, {
        method: 'DELETE',
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  if (loading) {
    return <div className="loading">Caricamento dashboard...</div>;
  }

  return (
    <div className="staff-dashboard">
      <div className="dashboard-header">
        <h1>Dashboard Staff</h1>
        <div className="header-controls">
          <div className="stats-display">
            <span>Ordini Oggi: {stats.total_orders}</span>
            <div className="payment-stats">
              <span className="cash-total">💰 Contanti: €{paymentTotals.contanti.toFixed(2)}</span>
              <span className="card-total">💳 Carta: €{paymentTotals.carta.toFixed(2)}</span>
              <span className="total-revenue">📊 Totale: €{(paymentTotals.contanti + paymentTotals.carta).toFixed(2)}</span>
            </div>
          </div>
          <div className="audio-control">
            <label className="audio-toggle">
              <input
                type="checkbox"
                checked={audioEnabled}
                onChange={(e) => setAudioEnabled(e.target.checked)}
              />
              <span className="audio-icon">
                {audioEnabled ? '🔔' : '🔇'}
              </span>
              <span className="audio-label">Audio</span>
            </label>
            
            <label className="audio-toggle notification-toggle">
              <input
                type="checkbox"
                checked={notificationsEnabled && notificationPermission === 'granted'}
                onChange={async (e) => {
                  if (e.target.checked) {
                    // Richiedi permesso se non già concesso
                    if (notificationPermission !== 'granted') {
                      const granted = await requestNotificationPermission();
                      if (granted) {
                        // Invia notifica di test
                        sendBrowserNotification(
                          '✅ Notifiche Attivate!',
                          { body: 'Riceverai notifiche per i nuovi ordini anche quando il sito è in background' }
                        );
                      }
                    } else {
                      setNotificationsEnabled(true);
                      localStorage.setItem('notifications_enabled', 'true');
                    }
                  } else {
                    setNotificationsEnabled(false);
                    localStorage.setItem('notifications_enabled', 'false');
                  }
                }}
              />
              <span className="audio-icon">
                {notificationsEnabled && notificationPermission === 'granted' ? '📬' : '📭'}
              </span>
              <span className="audio-label">
                Notifiche Browser
                {notificationPermission === 'denied' && (
                  <small style={{display: 'block', fontSize: '10px', color: '#dc3545'}}>
                    (bloccate)
                  </small>
                )}
              </span>
            </label>
            
            <label className="audio-toggle push-toggle">
              <input
                type="checkbox"
                checked={pushEnabled}
                disabled={!('serviceWorker' in navigator) || !('Notification' in window)}
                onChange={async (e) => {
                  if (e.target.checked) {
                    const success = await subscribeToPush();
                    if (!success) {
                      e.target.checked = false;
                    }
                  } else {
                    await unsubscribeFromPush();
                  }
                }}
              />
              <span className="audio-icon">
                {pushEnabled ? '📱' : '📴'}
              </span>
              <span className="audio-label">
                Push (Standby)
                {(!('serviceWorker' in navigator) || !('Notification' in window) || !('PushManager' in window)) && (
                  <small style={{display: 'block', fontSize: '10px', color: '#dc3545'}}>
                    (non supportato)
                  </small>
                )}
                {('serviceWorker' in navigator) && !window.isSecureContext && (
                  <small style={{display: 'block', fontSize: '10px', color: '#ff9800'}}>
                    (richiede HTTPS)
                  </small>
                )}
              </span>
            </label>
          </div>
          <button onClick={onLogout} className="logout-button">Logout</button>
        </div>
      </div>

      <div className="dashboard-nav">
        <button 
          className={currentView === 'orders' ? 'active' : ''}
          onClick={() => setCurrentView('orders')}
        >
          Ordini ({orders.filter(o => o.status === 'in_arrivo').length})
        </button>
        <button 
          className={currentView === 'products' ? 'active' : ''}
          onClick={() => {
            setCurrentView('products');
            loadViewData('products');
          }}
        >
          Prodotti
        </button>
        <button 
          className={currentView === 'history' ? 'active' : ''}
          onClick={() => {
            setCurrentView('history');
            loadViewData('history');
          }}
        >
          Storico
        </button>
        <button 
          className={currentView === 'settings' ? 'active' : ''}
          onClick={() => {
            setCurrentView('settings');
            loadViewData('settings');
          }}
        >
          Impostazioni
        </button>
      </div>

      <div className="dashboard-content">
        {currentView === 'orders' && <OrdersView orders={orders} updateOrderStatus={updateOrderStatus} btPrintOrder={btPrintOrder} printTraditional={printTraditional} btStatus={btStatus} btError={btError} orderPrintStatus={orderPrintStatus} />}
        {currentView === 'products' && (
          <ProductsView 
            products={products} 
            setProducts={setProducts}
            deleteProduct={deleteProduct}
            showAddProduct={showAddProduct}
            setShowAddProduct={setShowAddProduct}
            fetchData={fetchData}
            loadViewData={loadViewData}
            setDataCache={setDataCache}
          />
        )}
        {currentView === 'history' && <HistoryView history={history} />}
        {currentView === 'settings' && (
          <SettingsView 
            settings={settings} 
            toggleOrdersEnabled={toggleOrdersEnabled}
            updateTimeSettings={updateTimeSettings}
            autoPrintEnabled={autoPrintEnabled}
            toggleAutoPrint={toggleAutoPrint}
            btStatus={btStatus}
            btError={btError}
            btConnect={btConnect}
            btDisconnect={btDisconnect}
            btTestPrint={btTestPrint}
          />
        )}
      </div>
    </div>
  );
}

// Helper per mostrare liste di stringhe o oggetti { name: ... }
const formatOptionList = (list) => {
  if (!Array.isArray(list) || list.length === 0) return '';

  const first = list[0];

  // Caso nuovo: array di stringhe
  if (typeof first === 'string') {
    return list.join(', ');
  }

  // Caso vecchio: array di oggetti con { name, ... }
  if (first && typeof first === 'object' && 'name' in first) {
    return list.map(el => el.name).join(', ');
  }

  // Fallback estremo
  return String(list);
};

function OrdersView({ orders, updateOrderStatus, btPrintOrder, printTraditional, btStatus, btError, orderPrintStatus }) {
  const [showTraditional, setShowTraditional] = useState({});

  const formatOptionList = (list) => {
    if (!Array.isArray(list)) return '';
    return list
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item && typeof item === 'object') {
          const name = item.name || '';
          const qty = item.quantity && Number(item.quantity) > 1 ? ` x${Number(item.quantity)}` : '';
          return `${name}${qty}`.trim();
        }
        return '';
      })
      .filter(Boolean)
      .join(', ');
  };

  const formatMenuSauces = (s) => formatOptionList(s);
  const formatIngredients = (s) => formatOptionList(s);
  const formatPaniniSauces = (s) => formatOptionList(s);

  const getPrintLabel = (orderId) => {
    const st = orderPrintStatus[orderId];
    if (st === 'invio') return 'Invio...';
    if (st === 'inviato') return 'Inviato';
    if (st === 'errore') return 'Errore - Riprova';
    return 'Stampa';
  };

  const getPrintClass = (orderId) => {
    const st = orderPrintStatus[orderId];
    if (st === 'invio') return 'print-button print-sending';
    if (st === 'inviato') return 'print-button print-sent';
    if (st === 'errore') return 'print-button print-error';
    return 'print-button';
  };

  return (
    <div className="orders-section">
      <div className="orders-header-row">
        <h2>Ordini di Oggi</h2>
        <span className={`bt-badge bt-badge-${btStatus}`} data-testid="bt-badge">
          {btStatus === 'connesso' ? 'PT-210 connessa' : btStatus === 'connessione' ? 'Connessione...' : 'PT-210 disconnessa'}
        </span>
      </div>
      {btStatus !== 'connesso' && (
        <p className="bt-warning" data-testid="bt-warning">Stampante non connessa. Vai in Impostazioni per collegare la PT-210.</p>
      )}
      {orders.length === 0 ? (
        <p>Nessun ordine per oggi</p>
      ) : (
        <div className="orders-grid">
          {orders.map((order) => (
            <div key={order.id} className={`order-card ${order.status}`}>
              <div className="order-header">
                <span className="order-number">#{order.order_number}</span>
                <span className="order-time">
                  {(() => {
                    const utcDate = new Date(order.created_at + 'Z');
                    return utcDate.toLocaleTimeString('it-IT', {
                      timeZone: 'Europe/Rome',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit',
                    });
                  })()}
                </span>
              </div>

              <div className="order-customer">
                <strong>
                  {order.customer_info.car_model}{' '}
                  {order.customer_info.car_color}
                </strong>
                <span>Targa: {order.customer_info.license_plate}</span>
                <span>
                  Pagamento:{' '}
                  {order.customer_info.payment_method === 'contanti'
                    ? 'Contanti'
                    : 'Carta'}
                </span>
              </div>

              <div className="order-items">
                {order.items.map((item, idx) => (
                  <div key={idx}>
                    {item.quantity}x {item.product_name}
                    {item.selected_fries && (
                      <small> (Patatine: {item.selected_fries})</small>
                    )}
                    {item.selected_drink && (
                      <small> (Bibita: {item.selected_drink})</small>
                    )}
                    {item.selected_menu_sauces &&
                      item.selected_menu_sauces.length > 0 && (
                        <small>
                          {' '}
                          (Salse Menu:{' '}
                          {formatMenuSauces(item.selected_menu_sauces)})
                        </small>
                      )}
                    {item.selected_ingredients &&
                      item.selected_ingredients.length > 0 && (
                        <small>
                          {' '}
                          (Extra: {formatIngredients(item.selected_ingredients)})
                        </small>
                      )}
                    {item.selected_sauces &&
                      item.selected_sauces.length > 0 && (
                        <small>
                          {' '}
                          (Salse: {formatPaniniSauces(item.selected_sauces)})
                        </small>
                      )}
                  </div>
                ))}
              </div>

              <div className="order-total">
                Totale: EUR {order.total_amount.toFixed(2)}
              </div>

              <div className="order-actions">
                <button
                  onClick={() => btPrintOrder(order)}
                  className={getPrintClass(order.id)}
                  disabled={orderPrintStatus[order.id] === 'invio' || btStatus !== 'connesso'}
                  data-testid={`print-order-${order.id}`}
                >
                  {btStatus !== 'connesso' ? 'BT non connesso' : getPrintLabel(order.id)}
                </button>
                <button
                  onClick={() => setShowTraditional(p => ({ ...p, [order.id]: !p[order.id] }))}
                  className="print-traditional-toggle"
                  data-testid={`print-trad-toggle-${order.id}`}
                >
                  {showTraditional[order.id] ? 'Nascondi' : 'Stampa Tradizionale'}
                </button>
                {showTraditional[order.id] && (
                  <button
                    onClick={() => printTraditional(order)}
                    className="print-traditional-btn"
                    data-testid={`print-trad-${order.id}`}
                  >
                    Apri anteprima browser
                  </button>
                )}
                {order.status === 'in_arrivo' && (
                  <button
                    onClick={() => updateOrderStatus(order.id, 'consegnato')}
                    className="deliver-button"
                  >
                    Segna come Consegnato
                  </button>
                )}
                {order.status === 'consegnato' && (
                  <span className="status-delivered">Consegnato</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductsView({ products, setProducts, c, deleteProduct, showAddProduct, setShowAddProduct, fetchData, loadViewData, setDataCache }) {
  const [editingProduct, setEditingProduct] = useState(null);
  const [reordering, setReordering] = useState(false);

  // Funzione per riordinare un prodotto
  const reorderProduct = async (productId, direction) => {
    if (reordering) return; // Previeni click multipli
    
    setReordering(true);
    try {
      const token = localStorage.getItem('staff_token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/products/${productId}/reorder?direction=${direction}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Riordinamento completato:', result);
        
        // Forza il reload diretto dei prodotti bypassando la cache
        const headers = { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };
        
        const productsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/products`, { headers });
        if (productsRes.ok) {
          const productsData = await productsRes.json();
          
          // Aggiorna subito lo stato dei prodotti
          setProducts(productsData);
          
          // Aggiorna anche la cache con i nuovi dati
          setDataCache(prev => ({
            ...prev,
            products: { data: productsData, timestamp: Date.now() }
          }));
          
          console.log('✅ Prodotti ricaricati immediatamente con nuovo ordine');
        }
      } else {
        const error = await response.json();
        console.error('❌ Errore nel riordinamento del prodotto:', error);
        alert('Errore nel riordinamento del prodotto');
      }
    } catch (error) {
      console.error('❌ Errore:', error);
      alert('Errore di rete durante il riordinamento');
    } finally {
      setReordering(false);
    }
  };

  // Funzione per cambiare rapidamente la disponibilità
  const toggleAvailability = async (productId, currentAvailability) => {
    try {
      const token = localStorage.getItem('staff_token');
      const newAvailability = !currentAvailability;
      
      // Ottimistic update - aggiorna subito l'UI
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p.id === productId ? { ...p, available: newAvailability } : p
        )
      );
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/staff/products/${productId}/availability`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ available: newAvailability })
      });

      if (response.ok) {
        console.log(`✅ Disponibilità aggiornata: ${newAvailability ? 'Disponibile' : 'Non disponibile'}`);
        
        // Aggiorna anche la cache
        setDataCache(prev => ({
          ...prev,
          products: { 
            data: prev.products.data.map(p => 
              p.id === productId ? { ...p, available: newAvailability } : p
            ), 
            timestamp: Date.now() 
          }
        }));
      } else {
        // Rollback in caso di errore
        setProducts(prevProducts => 
          prevProducts.map(p => 
            p.id === productId ? { ...p, available: currentAvailability } : p
          )
        );
        console.error('❌ Errore nell\'aggiornamento della disponibilità');
        alert('Errore nell\'aggiornamento della disponibilità');
      }
    } catch (error) {
      // Rollback in caso di errore
      setProducts(prevProducts => 
        prevProducts.map(p => 
          p.id === productId ? { ...p, available: currentAvailability } : p
        )
      );
      console.error('❌ Errore:', error);
      alert('Errore di rete durante l\'aggiornamento');
    }
  };

  // Raggruppa prodotti per categoria
  const categorizeProducts = () => {
    const categories = {
      panini: { name: 'Panini', emoji: '🍔', products: [] },
      patatine: { name: 'Patatine', emoji: '🍟', products: [] },
      sfizi: { name: 'Sfizi', emoji: '🥨', products: [] },
      dolci:   { name: 'Dolci',   emoji: '🍰', products: [] },  // 👈 AGGIUNTO
      bibite: { name: 'Bibite', emoji: '🥤', products: [] },
      menu: { name: 'Menu', emoji: '🍽️', products: [] }
    };

    products.forEach(product => {
      const category = product.category || 'sfizi';
      if (categories[category]) {
        categories[category].products.push(product);
      }
    });

    // Sort products by display_order within each category
    Object.keys(categories).forEach(key => {
      categories[key].products.sort((a, b) => {
        const orderA = a.display_order !== undefined ? a.display_order : 999;
        const orderB = b.display_order !== undefined ? b.display_order : 999;
        return orderA - orderB;
      });
    });

    return categories;
  };

  const categorizedProducts = categorizeProducts();

  return (
    <div className="products-section">
      <div className="products-header">
        <h2>Gestione Prodotti</h2>
        <button 
          className="add-product-btn"
          onClick={() => setShowAddProduct(true)}
        >
          Aggiungi Prodotto
        </button>
      </div>
      
      {/* Mostra prodotti divisi per categoria */}
      {Object.entries(categorizedProducts).map(([categoryKey, categoryData]) => (
        categoryData.products.length > 0 && (
          <div key={categoryKey} className="category-section">
            <h3 className="category-title">
              {categoryData.emoji} {categoryData.name} ({categoryData.products.length})
            </h3>
            <div className="products-grid">
              {categoryData.products.map((product, index) => (
                <div key={product.id} className={`product-management-card ${reordering ? 'reordering' : ''}`}>
                  <div className="product-order-controls">
                    <button 
                      onClick={() => reorderProduct(product.id, 'up')}
                      disabled={index === 0 || reordering}
                      className="product-order-btn"
                      title="Sposta su"
                    >
                      {reordering ? '⏳' : '▲'}
                    </button>
                    <span className="product-order-number">{index + 1}</span>
                    <button 
                      onClick={() => reorderProduct(product.id, 'down')}
                      disabled={index === categoryData.products.length - 1 || reordering}
                      className="product-order-btn"
                      title="Sposta giù"
                    >
                      {reordering ? '⏳' : '▼'}
                    </button>
                  </div>
                  {product.image_base64 && (
                    <img src={`data:image/jpeg;base64,${product.image_base64}`} alt={product.name} />
                  )}
                  <h3>{product.name}</h3>
                  <p>{product.description}</p>
                  <p><strong>€{product.price.toFixed(2)}</strong></p>
                  
                  {/* Toggle disponibilità rapido */}
                  <div className="availability-toggle-container">
                    <span className="availability-label">Disponibilità:</span>
                    <button 
                      className={`availability-toggle-btn ${product.available ? 'available' : 'unavailable'}`}
                      onClick={() => toggleAvailability(product.id, product.available)}
                      title={product.available ? 'Clicca per rendere non disponibile' : 'Clicca per rendere disponibile'}
                    >
                      <span className="toggle-icon">{product.available ? '✅' : '❌'}</span>
                      <span className="toggle-text">{product.available ? 'Disponibile' : 'Non disponibile'}</span>
                    </button>
                  </div>
                  {product.stock_quantity != null && (
                    <div className="stock-indicator" data-testid={`stock-${product.id}`}>
                      <span className="stock-label">Scorte:</span>
                      <span className={`stock-value ${product.stock_quantity <= 0 ? 'stock-empty' : product.stock_quantity <= 5 ? 'stock-low' : 'stock-ok'}`}>
                        {product.stock_quantity}
                      </span>
                    </div>
                  )}
                  {product.menu_options && product.category === 'menu' && (
                    <div className="menu-options-display">
                      <small><strong>Opzioni Menu:</strong></small>
                      {product.menu_options.fries_options && product.menu_options.fries_options.length > 0 && (
                        <small>Patatine: {product.menu_options.fries_options.map(f => `${f.name} (+€${f.price_modifier})`).join(', ')}</small>
                      )}
                      {product.menu_options.drink_options && product.menu_options.drink_options.length > 0 && (
                        <small>Bibite: {product.menu_options.drink_options.map(d => `${d.name} (+€${d.price_modifier})`).join(', ')}</small>
                      )}
                    </div>
                  )}
                  {product.ingredients_options && product.category === 'patatine' && (
                    <div className="ingredients-options-display">
                      <small><strong>Ingredienti Extra:</strong></small>
                      <small>{product.ingredients_options.map(ing => `${ing.name} ${parseFloat(ing.price_modifier) > 0 ? `(+€${parseFloat(ing.price_modifier).toFixed(2)})` : '(Gratis)'}`).join(', ')}</small>
                    </div>
                  )}
                  {product.sauces_options && product.category === 'panini' && (
                    <div className="sauces-options-display">
                      <small><strong>Salse Disponibili:</strong></small>
                      <small>{product.sauces_options.map(sauce => `${sauce.name} ${parseFloat(sauce.price_modifier) > 0 ? `(+€${parseFloat(sauce.price_modifier).toFixed(2)})` : '(Gratis)'}`).join(', ')}</small>
                    </div>
                  )}
                  <div className="product-actions">
                    <button 
                      className="edit-product-btn"
                      onClick={() => setEditingProduct(product)}
                    >
                      Modifica
                    </button>
                    <button 
                      className="delete-product-btn"
                      onClick={() => deleteProduct(product.id)}
                    >
                      Elimina
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ))}

      {/* Messaggio se non ci sono prodotti */}
      {products.length === 0 && (
        <div className="no-products-message">
          <p>Nessun prodotto presente. Aggiungi il primo prodotto!</p>
        </div>
      )}

      {showAddProduct && (
        <AddProductModal 
          onClose={() => setShowAddProduct(false)}
          fetchData={fetchData}
        />
      )}

      {editingProduct && (
        <EditProductModal 
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          fetchData={fetchData}
        />
      )}
    </div>
  );
}

function EditProductModal({ product, onClose, fetchData }) {
  const [formData, setFormData] = useState({
    name: product.name || '',
    price: product.price || '',
    description: product.description || '',
    category: product.category || 'panini',
    available: product.available !== false,
    stock_enabled: product.stock_quantity != null,
    stock_quantity: product.stock_quantity != null ? String(product.stock_quantity) : ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuOptions, setMenuOptions] = useState({
    fries_options: product.menu_options?.fries_options || [{ name: 'Patatine Normali', price_modifier: 0 }],
    drink_options: product.menu_options?.drink_options || [{ name: 'Coca Cola', price_modifier: 0 }],
    sauces_options: product.menu_options?.sauces_options || [{ name: 'Nessuna salsa', price_modifier: 0 }, { name: 'Ketchup', price_modifier: 0 }]
  });
  const [ingredientsOptions, setIngredientsOptions] = useState(
    product.ingredients_options || [{ name: 'Ketchup', price_modifier: 0 }]
  );
  const [saucesOptions, setSaucesOptions] = useState(
    product.sauces_options || [{ name: 'Ketchup', price_modifier: 0 }]
  );

  const addOption = (type) => {
    setMenuOptions(prev => ({
      ...prev,
      [type]: [...prev[type], { name: '', price_modifier: 0 }]
    }));
  };

  const updateOption = (type, index, field, value) => {
    setMenuOptions(prev => ({
      ...prev,
      [type]: prev[type].map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    }));
  };

  const removeOption = (type, index) => {
    if (menuOptions[type].length > 1) {
      setMenuOptions(prev => ({
        ...prev,
        [type]: prev[type].filter((_, i) => i !== index)
      }));
    }
  };

  const moveOption = (type, index, direction) => {
    setMenuOptions(prev => {
      const newOptions = [...prev[type]];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (targetIndex < 0 || targetIndex >= newOptions.length) return prev;
      
      [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
      
      return {
        ...prev,
        [type]: newOptions
      };
    });
  };

  // Funzioni per gestire ingredienti delle patatine
  const addIngredient = () => {
    setIngredientsOptions(prev => [...prev, { name: '', price_modifier: 0 }]);
  };

  const updateIngredient = (index, field, value) => {
    setIngredientsOptions(prev => 
      prev.map((ingredient, i) => 
        i === index ? { ...ingredient, [field]: value } : ingredient
      )
    );
  };

  const removeIngredient = (index) => {
    if (ingredientsOptions.length > 1) {
      setIngredientsOptions(prev => prev.filter((_, i) => i !== index));
    }
  };

  const moveIngredient = (index, direction) => {
    setIngredientsOptions(prev => {
      const newIngredients = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (targetIndex < 0 || targetIndex >= newIngredients.length) return prev;
      
      [newIngredients[index], newIngredients[targetIndex]] = [newIngredients[targetIndex], newIngredients[index]];
      
      return newIngredients;
    });
  };

  // Funzioni per gestire salse dei panini
  const addSauce = () => {
    setSaucesOptions(prev => [...prev, { name: '', price_modifier: 0 }]);
  };

  const updateSauce = (index, field, value) => {
    setSaucesOptions(prev => 
      prev.map((sauce, i) => 
        i === index ? { ...sauce, [field]: value } : sauce
      )
    );
  };

  const removeSauce = (index) => {
    if (saucesOptions.length > 1) {
      setSaucesOptions(prev => prev.filter((_, i) => i !== index));
    }
  };

  const moveSauce = (index, direction) => {
    setSaucesOptions(prev => {
      const newSauces = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (targetIndex < 0 || targetIndex >= newSauces.length) return prev;
      
      [newSauces[index], newSauces[targetIndex]] = [newSauces[targetIndex], newSauces[index]];
      
      return newSauces;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('staff_token');
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('price', parseFloat(formData.price));
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('available', formData.available);
      // Stock quantity
      if (formData.stock_enabled && formData.stock_quantity !== '') {
        formDataToSend.append('stock_quantity', parseInt(formData.stock_quantity));
      } else {
        formDataToSend.append('stock_quantity', '');
      }
      
      // Add menu options if category is menu
      if (formData.category === 'menu') {
        const cleanedOptions = {
          fries_options: menuOptions.fries_options.filter(f => f.name.trim() !== ''),
          drink_options: menuOptions.drink_options.filter(d => d.name.trim() !== ''),
          sauces_options: menuOptions.sauces_options.filter(s => s.name.trim() !== '')
        };
        formDataToSend.append('menu_options', JSON.stringify(cleanedOptions));
      }

      // Add ingredients options if category is patatine
      if (formData.category === 'patatine') {
        const cleanedIngredients = ingredientsOptions.filter(ing => ing.name.trim() !== '');
        formDataToSend.append('ingredients_options', JSON.stringify(cleanedIngredients));
      }

      // Add sauces options if category is panini
      if (formData.category === 'panini') {
        const cleanedSauces = saucesOptions.filter(sauce => sauce.name.trim() !== '');
        formDataToSend.append('sauces_options', JSON.stringify(cleanedSauces));
      }
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      const response = await fetch(`${BACKEND_URL}/api/staff/products/${product.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        fetchData();
        onClose();
      } else {
        alert('Errore durante la modifica del prodotto');
      }
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Errore di connessione');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-product-modal" onClick={e => e.stopPropagation()}>
        <h2>Modifica Prodotto</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Prezzo Base (€) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>
          
          <div className="form-group">
            <label>Categoria *</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
            >
              <option value="panini">Panini</option>
              <option value="patatine">Patatine</option>
              <option value="sfizi">Sfizi</option>
              <option value="dolci">Dolci</option> 
              <option value="bibite">Bibite</option>
              <option value="menu">Menu</option>
            </select>
          </div>

          <div className="form-group stock-group">
            <label>
              <input
                type="checkbox"
                checked={formData.stock_enabled}
                onChange={(e) => setFormData({...formData, stock_enabled: e.target.checked, stock_quantity: e.target.checked ? formData.stock_quantity : ''})}
              />
              {' '}Gestisci quantità disponibile
            </label>
            {formData.stock_enabled && (
              <input
                type="number"
                min="0"
                placeholder="Quantità disponibile"
                value={formData.stock_quantity}
                onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                style={{ marginTop: '8px' }}
              />
            )}
            <small className="setting-description">
              Se attivo, la quantità diminuisce ad ogni ordine. A 0 il prodotto diventa non disponibile.
            </small>
          </div>

          
          {formData.category === 'menu' && (
            <div className="menu-options-section">
              <h3>Opzioni Menu</h3>
              
              <div className="option-group">
                <h4>🍟 Opzioni Patatine</h4>
                {menuOptions.fries_options.map((option, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveOption('fries_options', index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveOption('fries_options', index, 'down')}
                        className="order-btn"
                        disabled={index === menuOptions.fries_options.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome opzione (es. Patatine con Würstel)"
                        value={option.name}
                        onChange={(e) => updateOption('fries_options', index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={option.price_modifier}
                        onChange={(e) => updateOption('fries_options', index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {menuOptions.fries_options.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeOption('fries_options', index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={() => addOption('fries_options')}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Opzione Patatine
                </button>
              </div>
              
              <div className="option-group">
                <h4>🥤 Opzioni Bibite</h4>
                {menuOptions.drink_options.map((option, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveOption('drink_options', index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveOption('drink_options', index, 'down')}
                        className="order-btn"
                        disabled={index === menuOptions.drink_options.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome bibita (es. Birra)"
                        value={option.name}
                        onChange={(e) => updateOption('drink_options', index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={option.price_modifier}
                        onChange={(e) => updateOption('drink_options', index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {menuOptions.drink_options.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeOption('drink_options', index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={() => addOption('drink_options')}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Opzione Bibita
                </button>
              </div>

              <div className="option-group">
                <h4>🍯 Salse Disponibili</h4>
                {menuOptions.sauces_options.map((option, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveOption('sauces_options', index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveOption('sauces_options', index, 'down')}
                        className="order-btn"
                        disabled={index === menuOptions.sauces_options.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome salsa (es. Ketchup)"
                        value={option.name}
                        onChange={(e) => updateOption('sauces_options', index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={option.price_modifier}
                        onChange={(e) => updateOption('sauces_options', index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {menuOptions.sauces_options.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeOption('sauces_options', index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={() => addOption('sauces_options')}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Salsa
                </button>
              </div>
            </div>
          )}

          {formData.category === 'patatine' && (
            <div className="ingredients-options-section">
              <h3>Ingredienti Extra per Patatine</h3>
              <p><small>Configura gli ingredienti che i clienti possono aggiungere alle patatine</small></p>
              
              <div className="option-group">
                <h4>🧀 Ingredienti Disponibili</h4>
                {ingredientsOptions.map((ingredient, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveIngredient(index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveIngredient(index, 'down')}
                        className="order-btn"
                        disabled={index === ingredientsOptions.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome ingrediente (es. Formaggio Cheddar)"
                        value={ingredient.name}
                        onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={ingredient.price_modifier}
                        onChange={(e) => updateIngredient(index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {ingredientsOptions.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeIngredient(index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={addIngredient}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Ingrediente
                </button>
              </div>
            </div>
          )}

          {formData.category === 'panini' && (
            <div className="sauces-options-section">
              <h3>Salse Extra per Panini</h3>
              <p><small>Configura le salse che i clienti possono aggiungere ai panini</small></p>
              
              <div className="option-group">
                <h4>🌶️ Salse Disponibili</h4>
                {saucesOptions.map((sauce, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveSauce(index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveSauce(index, 'down')}
                        className="order-btn"
                        disabled={index === saucesOptions.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome salsa (es. Salsa BBQ)"
                        value={sauce.name}
                        onChange={(e) => updateSauce(index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={sauce.price_modifier}
                        onChange={(e) => updateSauce(index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {saucesOptions.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeSauce(index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={addSauce}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Salsa
                </button>
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label>Nuova Immagine (opzionale)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
            />
            <small>Lascia vuoto per mantenere l'immagine attuale</small>
          </div>
          
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.available}
                onChange={(e) => setFormData({...formData, available: e.target.checked})}
              />
              Disponibile
            </label>
          </div>
          
          <div className="modal-buttons">
            <button type="button" onClick={onClose}>Annulla</button>
            <button type="submit" disabled={loading}>
              {loading ? 'Aggiornamento...' : 'Aggiorna Prodotto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddProductModal({ onClose, fetchData }) {
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    category: 'panini',
    available: true,
    stock_enabled: false,
    stock_quantity: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [menuOptions, setMenuOptions] = useState({
    fries_options: [{ name: 'Patatine Normali', price_modifier: 0 }],
    drink_options: [{ name: 'Coca Cola', price_modifier: 0 }],
    sauces_options: [{ name: 'Nessuna salsa', price_modifier: 0 }, { name: 'Ketchup', price_modifier: 0 }]
  });
  const [ingredientsOptions, setIngredientsOptions] = useState([
    { name: 'Ketchup', price_modifier: 0 },
    { name: 'Maionese', price_modifier: 0 }
  ]);
  const [saucesOptions, setSaucesOptions] = useState([
    { name: 'Ketchup', price_modifier: 0 },
    { name: 'Maionese', price_modifier: 0 },
    { name: 'Senape', price_modifier: 0 }
  ]);

  const addOption = (type) => {
    setMenuOptions(prev => ({
      ...prev,
      [type]: [...prev[type], { name: '', price_modifier: 0 }]
    }));
  };

  const updateOption = (type, index, field, value) => {
    setMenuOptions(prev => ({
      ...prev,
      [type]: prev[type].map((option, i) => 
        i === index ? { ...option, [field]: value } : option
      )
    }));
  };

  const removeOption = (type, index) => {
    if (menuOptions[type].length > 1) {
      setMenuOptions(prev => ({
        ...prev,
        [type]: prev[type].filter((_, i) => i !== index)
      }));
    }
  };

  const moveOption = (type, index, direction) => {
    setMenuOptions(prev => {
      const newOptions = [...prev[type]];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (targetIndex < 0 || targetIndex >= newOptions.length) return prev;
      
      [newOptions[index], newOptions[targetIndex]] = [newOptions[targetIndex], newOptions[index]];
      
      return {
        ...prev,
        [type]: newOptions
      };
    });
  };

  // Funzioni per gestire ingredienti delle patatine
  const addIngredient = () => {
    setIngredientsOptions(prev => [...prev, { name: '', price_modifier: 0 }]);
  };

  const updateIngredient = (index, field, value) => {
    setIngredientsOptions(prev => 
      prev.map((ingredient, i) => 
        i === index ? { ...ingredient, [field]: value } : ingredient
      )
    );
  };

  const removeIngredient = (index) => {
    if (ingredientsOptions.length > 1) {
      setIngredientsOptions(prev => prev.filter((_, i) => i !== index));
    }
  };

  const moveIngredient = (index, direction) => {
    setIngredientsOptions(prev => {
      const newIngredients = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (targetIndex < 0 || targetIndex >= newIngredients.length) return prev;
      
      [newIngredients[index], newIngredients[targetIndex]] = [newIngredients[targetIndex], newIngredients[index]];
      
      return newIngredients;
    });
  };

  // Funzioni per gestire salse dei panini
  const addSauce = () => {
    setSaucesOptions(prev => [...prev, { name: '', price_modifier: 0 }]);
  };

  const updateSauce = (index, field, value) => {
    setSaucesOptions(prev => 
      prev.map((sauce, i) => 
        i === index ? { ...sauce, [field]: value } : sauce
      )
    );
  };

  const removeSauce = (index) => {
    if (saucesOptions.length > 1) {
      setSaucesOptions(prev => prev.filter((_, i) => i !== index));
    }
  };

  const moveSauce = (index, direction) => {
    setSaucesOptions(prev => {
      const newSauces = [...prev];
      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      
      if (targetIndex < 0 || targetIndex >= newSauces.length) return prev;
      
      [newSauces[index], newSauces[targetIndex]] = [newSauces[targetIndex], newSauces[index]];
      
      return newSauces;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('staff_token');
      const formDataToSend = new FormData();
      
      formDataToSend.append('name', formData.name);
      formDataToSend.append('price', parseFloat(formData.price));
      formDataToSend.append('description', formData.description);
      formDataToSend.append('category', formData.category);
      formDataToSend.append('available', formData.available);
      // Stock quantity
      if (formData.stock_enabled && formData.stock_quantity !== '') {
        formDataToSend.append('stock_quantity', parseInt(formData.stock_quantity));
      } else {
        formDataToSend.append('stock_quantity', '');
      }
      
      // Add menu options if category is menu
      if (formData.category === 'menu') {
        const cleanedOptions = {
          fries_options: menuOptions.fries_options.filter(f => f.name.trim() !== ''),
          drink_options: menuOptions.drink_options.filter(d => d.name.trim() !== ''),
          sauces_options: menuOptions.sauces_options.filter(s => s.name.trim() !== '')
        };
        formDataToSend.append('menu_options', JSON.stringify(cleanedOptions));
      }

      // Add ingredients options if category is patatine
      if (formData.category === 'patatine') {
        const cleanedIngredients = ingredientsOptions.filter(ing => ing.name.trim() !== '');
        formDataToSend.append('ingredients_options', JSON.stringify(cleanedIngredients));
      }

      // Add sauces options if category is panini
      if (formData.category === 'panini') {
        const cleanedSauces = saucesOptions.filter(sauce => sauce.name.trim() !== '');
        formDataToSend.append('sauces_options', JSON.stringify(cleanedSauces));
      }
      
      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      const response = await fetch(`${BACKEND_URL}/api/staff/products`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        fetchData();
        onClose();
      } else {
        alert('Errore durante la creazione del prodotto');
      }
    } catch (error) {
      console.error('Error creating product:', error);
      alert('Errore di connessione');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content add-product-modal" onClick={e => e.stopPropagation()}>
        <h2>Aggiungi Nuovo Prodotto</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Nome *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Prezzo Base (€) *</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({...formData, price: e.target.value})}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Descrizione</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>
          
          <div className="form-group">
            <label>Categoria *</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
            >
              <option value="panini">Panini</option>
              <option value="patatine">Patatine</option>
              <option value="sfizi">Sfizi</option>
              <option value="dolci">Dolci</option>
              <option value="bibite">Bibite</option>
              <option value="menu">Menu</option>
            </select>
          </div>

          <div className="form-group stock-group">
            <label>
              <input
                type="checkbox"
                checked={formData.stock_enabled}
                onChange={(e) => setFormData({...formData, stock_enabled: e.target.checked, stock_quantity: e.target.checked ? formData.stock_quantity : ''})}
              />
              {' '}Gestisci quantità disponibile
            </label>
            {formData.stock_enabled && (
              <input
                type="number"
                min="0"
                placeholder="Quantità disponibile"
                value={formData.stock_quantity}
                onChange={(e) => setFormData({...formData, stock_quantity: e.target.value})}
                style={{ marginTop: '8px' }}
              />
            )}
            <small className="setting-description">
              Se attivo, la quantità diminuisce ad ogni ordine. A 0 il prodotto diventa non disponibile.
            </small>
          </div>

          
          {formData.category === 'menu' && (
            <div className="menu-options-section">
              <h3>Opzioni Menu (Solo per gestione Staff)</h3>
              <p><small>Queste opzioni sono visibili solo nell'area staff per la configurazione</small></p>
              
              <div className="option-group">
                <h4>🍟 Opzioni Patatine</h4>
                {menuOptions.fries_options.map((option, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveOption('fries_options', index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveOption('fries_options', index, 'down')}
                        className="order-btn"
                        disabled={index === menuOptions.fries_options.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome opzione (es. Patatine con Würstel)"
                        value={option.name}
                        onChange={(e) => updateOption('fries_options', index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={option.price_modifier}
                        onChange={(e) => updateOption('fries_options', index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {menuOptions.fries_options.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeOption('fries_options', index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={() => addOption('fries_options')}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Opzione Patatine
                </button>
              </div>
              
              <div className="option-group">
                <h4>🥤 Opzioni Bibite</h4>
                {menuOptions.drink_options.map((option, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveOption('drink_options', index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveOption('drink_options', index, 'down')}
                        className="order-btn"
                        disabled={index === menuOptions.drink_options.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome bibita (es. Birra)"
                        value={option.name}
                        onChange={(e) => updateOption('drink_options', index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={option.price_modifier}
                        onChange={(e) => updateOption('drink_options', index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {menuOptions.drink_options.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeOption('drink_options', index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={() => addOption('drink_options')}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Opzione Bibita
                </button>
              </div>

              <div className="option-group">
                <h4>🍯 Salse Disponibili</h4>
                {menuOptions.sauces_options.map((option, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveOption('sauces_options', index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveOption('sauces_options', index, 'down')}
                        className="order-btn"
                        disabled={index === menuOptions.sauces_options.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome salsa (es. Ketchup)"
                        value={option.name}
                        onChange={(e) => updateOption('sauces_options', index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={option.price_modifier}
                        onChange={(e) => updateOption('sauces_options', index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {menuOptions.sauces_options.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeOption('sauces_options', index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={() => addOption('sauces_options')}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Salsa
                </button>
              </div>
            </div>
          )}

          {formData.category === 'patatine' && (
            <div className="ingredients-options-section">
              <h3>Ingredienti Extra per Patatine</h3>
              <p><small>Configura gli ingredienti che i clienti possono aggiungere alle patatine</small></p>
              
              <div className="option-group">
                <h4>🧀 Ingredienti Disponibili</h4>
                {ingredientsOptions.map((ingredient, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveIngredient(index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveIngredient(index, 'down')}
                        className="order-btn"
                        disabled={index === ingredientsOptions.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome ingrediente (es. Formaggio Cheddar)"
                        value={ingredient.name}
                        onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={ingredient.price_modifier}
                        onChange={(e) => updateIngredient(index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {ingredientsOptions.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeIngredient(index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={addIngredient}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Ingrediente
                </button>
              </div>
            </div>
          )}

          {formData.category === 'panini' && (
            <div className="sauces-options-section">
              <h3>Salse Extra per Panini</h3>
              <p><small>Configura le salse che i clienti possono aggiungere ai panini</small></p>
              
              <div className="option-group">
                <h4>🌶️ Salse Disponibili</h4>
                {saucesOptions.map((sauce, index) => (
                  <div key={index} className="option-item-enhanced">
                    <div className="order-controls">
                      <button 
                        type="button" 
                        onClick={() => moveSauce(index, 'up')}
                        className="order-btn"
                        disabled={index === 0}
                        title="Sposta su"
                      >
                        ▲
                      </button>
                      <span className="order-number">{index + 1}</span>
                      <button 
                        type="button" 
                        onClick={() => moveSauce(index, 'down')}
                        className="order-btn"
                        disabled={index === saucesOptions.length - 1}
                        title="Sposta giù"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="option-inputs">
                      <input
                        type="text"
                        placeholder="Nome salsa (es. Salsa BBQ)"
                        value={sauce.name}
                        onChange={(e) => updateSauce(index, 'name', e.target.value)}
                        className="option-name-input"
                      />
                      <input
                        type="number"
                        step="0.01"
                        placeholder="€"
                        value={sauce.price_modifier}
                        onChange={(e) => updateSauce(index, 'price_modifier', parseFloat(e.target.value) || 0)}
                        className="option-price-input"
                      />
                    </div>
                    {saucesOptions.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeSauce(index)}
                        className="remove-option-btn"
                        title="Rimuovi"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                ))}
                <button 
                  type="button" 
                  onClick={addSauce}
                  className="add-option-btn"
                >
                  ➕ Aggiungi Salsa
                </button>
              </div>
            </div>
          )}
          
          <div className="form-group">
            <label>Immagine</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setImageFile(e.target.files[0])}
            />
          </div>
          
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.available}
                onChange={(e) => setFormData({...formData, available: e.target.checked})}
              />
              Disponibile
            </label>
          </div>
          
          <div className="modal-buttons">
            <button type="button" onClick={onClose}>Annulla</button>
            <button type="submit" disabled={loading}>
              {loading ? 'Creazione...' : 'Crea Prodotto'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function HistoryView({ history }) {
  return (
    <div className="history-section">
      <h2>Riepilogo Giorni Precedenti</h2>
      {history.length === 0 ? (
        <p>Nessun dato storico disponibile</p>
      ) : (
        <div className="history-table">
          <div className="table-header">
            <span>Data</span>
            <span>Ordini</span>
            <span>Incassi</span>
          </div>
          {history.map((day, index) => (
            <div key={index} className="table-row">
              <span data-label="Data">{new Date(day.date).toLocaleDateString('it-IT')}</span>
              <span data-label="Ordini">{day.total_orders}</span>
              <span data-label="Incassi">€{day.total_revenue.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsView({ settings, toggleOrdersEnabled, updateTimeSettings, autoPrintEnabled, toggleAutoPrint, btStatus, btError, btConnect, btDisconnect, btTestPrint }) {
  const [timeSettings, setTimeSettings] = useState({
    start_time: settings.start_time,
    end_time: settings.end_time
  });
  const [updateMessage, setUpdateMessage] = useState('');
  const [testPrinting, setTestPrinting] = useState(false);

  const handleTimeUpdate = async () => {
    await updateTimeSettings({
      ...settings,
      ...timeSettings
    });
    setUpdateMessage('✅ Orari aggiornati con successo!');
    setTimeout(() => setUpdateMessage(''), 3000);
  };

  const getTimeDescription = (startTime, endTime) => {
    const start = startTime || '18:00';
    const end = endTime || '23:00';
    
    if (start <= end) {
      return `Servizio attivo dalle ${start} alle ${end} (stesso giorno - fuso orario UTC+2)`;
    } else {
      return `Servizio attivo dalle ${start} alle ${end} (giorno seguente - fuso orario UTC+2)`;
    }
  };

  const getCurrentTime = () => {
    // Get current time with automatic Italian timezone (CET/CEST)
    const now = new Date();
    return now.toLocaleTimeString('it-IT', { 
      timeZone: 'Europe/Rome', // Gestisce automaticamente ora legale/solare
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit',
      hour12: false 
    });
  };

  return (
    <div className="settings-section">
      <h2>Impostazioni Sistema</h2>
      
      <div className="settings-card">
        <div className="setting-item">
          <label>
            <input
              type="checkbox"
              checked={settings.orders_enabled}
              onChange={toggleOrdersEnabled}
            />
            Abilita Ordinazioni
          </label>
          <p className="setting-description">
            {settings.orders_enabled ? 
              'Le ordinazioni sono attive' : 
              'Le ordinazioni sono sospese'}
          </p>
        </div>

        <div className="setting-item">
          <label>
            <input
              type="checkbox"
              checked={autoPrintEnabled}
              onChange={(e) => toggleAutoPrint(e.target.checked)}
              data-testid="auto-print-toggle"
            />
            Stampa Automatica Ordini
          </label>
          <p className="setting-description">
            {autoPrintEnabled ? 
              'Ogni nuovo ordine viene stampato automaticamente' : 
              'Stampa solo manuale dal pulsante sull\'ordine'}
          </p>
        </div>

        <div className="setting-item bt-section">
          <h3>Stampante Bluetooth PT-210</h3>
          <div className="bt-status-row">
            <span className={`bt-indicator bt-${btStatus}`} data-testid="bt-status-indicator"></span>
            <span className="bt-status-text" data-testid="bt-status-text">
              {btStatus === 'disconnesso' && 'Disconnessa'}
              {btStatus === 'connessione' && 'Connessione in corso...'}
              {btStatus === 'connesso' && 'Connessa'}
              {btStatus === 'errore' && 'Errore'}
            </span>
          </div>
          {btError && (
            <p className="bt-error" data-testid="bt-error">{btError}</p>
          )}
          <div className="bt-actions">
            {btStatus !== 'connesso' ? (
              <>
                <button
                  className="bt-connect-btn"
                  onClick={() => btConnect(false)}
                  disabled={btStatus === 'connessione'}
                  data-testid="bt-connect-btn"
                >
                  {btStatus === 'connessione' ? 'Connessione...' : 'COLLEGA PT-210'}
                </button>
                <button
                  className="bt-connect-all-btn"
                  onClick={() => btConnect(true)}
                  disabled={btStatus === 'connessione'}
                  data-testid="bt-connect-all-btn"
                >
                  CERCA TUTTI
                </button>
              </>
            ) : (
              <button
                className="bt-disconnect-btn"
                onClick={btDisconnect}
                data-testid="bt-disconnect-btn"
              >
                DISCONNETTI
              </button>
            )}
            <button
              className="bt-test-btn"
              onClick={async () => {
                setTestPrinting(true);
                await btTestPrint();
                setTestPrinting(false);
              }}
              disabled={btStatus !== 'connesso' || testPrinting}
              data-testid="bt-test-print-btn"
            >
              {testPrinting ? 'Stampa in corso...' : 'STAMPA DI PROVA'}
            </button>
          </div>
        </div>
        
        <div className="setting-item">
          <h3>Orari di Servizio</h3>
          <p className="current-time">Ora attuale (Italia): {getCurrentTime()}</p>
          <p className="setting-description">
            {getTimeDescription(timeSettings.start_time, timeSettings.end_time)}
          </p>
          <div className="time-settings">
            <div className="time-input">
              <label>Orario Inizio (Ora Italia):</label>
              <input
                type="time"
                value={timeSettings.start_time}
                onChange={(e) => setTimeSettings({...timeSettings, start_time: e.target.value})}
              />
            </div>
            <div className="time-input">
              <label>Orario Fine (Ora Italia):</label>
              <input
                type="time"
                value={timeSettings.end_time}
                onChange={(e) => setTimeSettings({...timeSettings, end_time: e.target.value})}
              />
            </div>
            <p className="timezone-info">
              ⏰ Gli orari si adattano automaticamente all'ora legale/solare italiana
            </p>
            <button onClick={handleTimeUpdate} className="update-time-btn">
              Aggiorna Orari
            </button>
          </div>
          {updateMessage && (
            <div className="update-message success">
              {updateMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [pageData, setPageData] = useState({});
  const [showLogin, setShowLogin] = useState(false);
  const { isAuthenticated, loading, login, logout } = useAuth();

  const navigate = (page, data = {}) => {
    setCurrentPage(page);
    setPageData(data);
  };

  if (loading) {
    return <div className="loading">Caricamento...</div>;
  }

  if (isAuthenticated) {
    return <StaffDashboard onLogout={logout} />;
  }

  return (
    <div className="app">
      {currentPage === 'home' && (
        <HomePage 
          onNavigate={navigate} 
          onShowLogin={() => setShowLogin(true)} 
        />
      )}
      
      {currentPage === 'order' && (
        <OrderPage onNavigate={navigate} ordersEnabled={pageData.ordersEnabled !== false} />
      )}
      
      {currentPage === 'checkout' && (
        <CheckoutPage onNavigate={navigate} checkoutData={pageData} />
      )}
      
      {currentPage === 'confirmation' && (
        <ConfirmationPage onNavigate={navigate} confirmationData={pageData} />
      )}

      <LoginModal 
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onLogin={login}
      />
    </div>
  );
}

export default function AppWithAuth() {
  return (
    <AuthProvider>
      <App />
    </AuthProvider>
  );
}